import React, { useState, useEffect } from 'react';
import UnifiedLandlordFlow, { LandlordAddPropertyCard } from './UnifiedLandlordFlow';
import ArchivedRecordsPanel from './ArchivedRecordsPanel';
import BusinessContactsPanel from './BusinessContactsPanel';
import './Settings.css';
import { formatDate } from '../utils/dates';
import { formatInteger } from '../utils/currency';
import ComplianceHandlingSettings from './ComplianceHandlingSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless

// 2026-02 — WhatsApp soft-disable feature flag. Defaults to false so the
// entire WhatsApp UI is hidden across Settings, dashboard, sidebar and
// notifications. Set REACT_APP_WHATSAPP_ENABLED=true in frontend/.env to
// reactivate every WhatsApp UI element without any code change. The
// backend routes, models and integrations remain intact at all times.
const WHATSAPP_ENABLED = String(process.env.REACT_APP_WHATSAPP_ENABLED || 'false').toLowerCase() === 'true';

// Step 52 BUG 4 — Sub-component listing properties linked to a landlord.
// Renders a small table with address, tenancy status (occupied / vacant) and
// a link to the property detail page. Uses the existing landlord_id link
// established in Step 17.
// =============================================================================
// LandlordEditAddProperty — 2026-05-29
// Shown on the Edit-Landlord form so agents can add new Let/Available
// properties to an existing landlord. Uses the same SectionB card as
// the unified Add-Landlord flow.
// =============================================================================
function LandlordEditAddProperty({ landlordId, apiBase, onSaved }) {
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([Date.now()]);
  return (
    <div style={{ marginTop: '1.25rem' }}
         data-testid="landlord-edit-add-property">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="btn-edit-landlord-add-property"
          style={{
            background: '#0073EA', color: '#fff', border: 'none',
            padding: '0.55rem 1.1rem', borderRadius: 6,
            fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
          }}>
          + Add Property to this Landlord
        </button>
      ) : (
        <div>
          {cards.map((k, idx) => (
            <LandlordAddPropertyCard
              key={k}
              index={idx}
              landlordId={landlordId}
              apiBase={apiBase}
              onSaved={onSaved}
            />
          ))}
          <div style={{ display: 'flex', gap: '0.5rem',
                        marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setCards(c => [...c, Date.now()])}
              data-testid="btn-edit-landlord-add-another"
              style={{
                background: '#0073EA', color: '#fff', border: 'none',
                padding: '0.45rem 0.9rem', borderRadius: 6,
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              }}>
              + Add Another Property
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setCards([Date.now()]); }}
              data-testid="btn-edit-landlord-add-done"
              style={{
                background: '#fff', color: '#0f172a',
                border: '1px solid #cbd5e1',
                padding: '0.45rem 0.9rem', borderRadius: 6,
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              }}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LandlordLinkedProperties({ landlordId, apiBase }) {
  const [props, setProps] = useState(null);
  const [err, setErr] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  useEffect(() => {
    if (!landlordId) return;
    let abort = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/landlords/${landlordId}/properties`, { credentials: 'include' });
        const txt = await res.text();
        const data = txt ? JSON.parse(txt) : [];
        if (!res.ok) {
          if (!abort) setErr(data.detail || 'Failed to load linked properties');
          return;
        }
        if (!abort) setProps(data);
      } catch (e) {
        if (!abort) setErr('Network error: ' + e.message);
      }
    })();
    return () => { abort = true; };
  }, [landlordId, apiBase]);

  // 2026-05-29 — Remove a duplicate / mis-linked property directly from
  // the landlord profile. Server refuses if a tenancy / active case /
  // open maintenance exists.
  const removeProperty = async (p) => {
    const label = `${p.address_line1}${p.postcode ? ' ' + p.postcode : ''}`;
    if (!window.confirm(
      `Remove this property?\n\n${label}\n\n` +
      `This permanently deletes the property record. Cannot be undone. ` +
      `Refused if the property has an active tenancy, open Section 8 ` +
      `case, or any open maintenance request.`)) {
      return;
    }
    setRemovingId(p._id);
    try {
      const res = await fetch(`${apiBase}/api/properties/${p._id}`,
        { method: 'DELETE', credentials: 'include' });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        window.alert('Could not remove property: ' +
          (data.detail || 'unknown error'));
        return;
      }
      setProps(prev => (prev || []).filter(x => x._id !== p._id));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div data-testid="landlord-linked-properties" style={{ marginTop: '1.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
      <h4 style={{ marginTop: 0, marginBottom: '0.6rem' }}>Let Properties</h4>
      {err && <p style={{ color: '#dc2626', fontSize: '0.9rem' }}>{err}</p>}
      {!err && props === null && <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading…</p>}
      {!err && Array.isArray(props) && props.length === 0 && (
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>This landlord has no let properties yet.</p>
      )}
      {!err && Array.isArray(props) && props.length > 0 && (
        <table className="landlord-table" style={{ width: '100%', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Address</th>
              <th style={{ textAlign: 'left' }}>Tenancy Status</th>
              <th style={{ textAlign: 'left' }}>Lead Tenant</th>
              <th style={{ textAlign: 'left' }}>Detail</th>
              <th style={{ textAlign: 'left' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {props.map(p => {
              // Step 53 BUG 7 — most relevant tab: tenancy if occupied, else compliance.
              // For occupied properties, route to the lead tenant's detail
              // page (which IS the property detail page when there's an
              // active tenancy). For vacant properties, route to the most
              // recent tenant if any; otherwise a no-op fallback.
              const tab = p.tenancy_status === 'occupied' ? 'tenancy' : 'compliance';
              const target = p.lead_tenant_id
                ? `/tenants/${p.lead_tenant_id}?tab=${tab}`
                : `#`;
              return (
                <tr key={p._id} data-testid={`linked-property-row-${p._id}`}>
                  <td>{p.address_line1}{p.city ? `, ${p.city}` : ''}{p.postcode ? ` ${p.postcode}` : ''}</td>
                  <td>
                    <span style={{
                      background: p.tenancy_status === 'occupied' ? '#dcfce7' : '#fef3c7',
                      color: p.tenancy_status === 'occupied' ? '#166534' : '#92400e',
                      padding: '0.2rem 0.55rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
                    }}>
                      {p.tenancy_status === 'occupied' ? 'Occupied' : 'Vacant'}
                    </span>
                  </td>
                  <td>{p.lead_tenant_name || '—'}</td>
                  <td>
                    {p.lead_tenant_id ? (
                      <a href={target} style={{ color: '#0073EA' }} data-testid={`linked-property-link-${p._id}`}>
                        View property
                      </a>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }} data-testid={`linked-property-novacant-${p._id}`}>No tenancy on record</span>
                    )}
                  </td>
                  <td>
                    {/* 2026-05-29 — Remove button on the landlord property
                        list. Refused if active tenancy / case / open maint. */}
                    <button
                      type="button"
                      onClick={() => removeProperty(p)}
                      disabled={removingId === p._id}
                      data-testid={`landlord-property-remove-${p._id}`}
                      style={{
                        padding: '0.3rem 0.6rem',
                        background: '#fff',
                        color: '#b91c1c',
                        border: '1px solid #fecaca',
                        borderRadius: 6,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: removingId === p._id ? 'wait' : 'pointer',
                      }}>
                      {removingId === p._id ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


const Settings = ({ initialTab, hideTabs = false, onOpenOnboarding }) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'email');
  // Step 45 — when the parent changes initialTab (e.g. user clicked a
  // different setup-checklist item) re-sync the active tab so the deep
  // link always lands on the correct tab.
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // 2026-02 — Resend transactional Test Send button. Sends a test email
  // via /api/email/send-transactional-test (same Resend path as signup
  // verification + password-reset emails) and renders an inline green
  // or red panel below the button so the agent can confirm
  // deliverability of system emails before going live.
  const [transactionalTesting, setTransactionalTesting] = useState(false);
  const [transactionalResult, setTransactionalResult] = useState(null); // { ok, channel, message_id, to, error }
  
  // Email connection settings
  const [emailAddress, setEmailAddress] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [hasPassword, setHasPassword] = useState(false);
  
  // Email preferences
  const [senderName, setSenderName] = useState('');
  const [emailTone, setEmailTone] = useState('professional');
  const [signoffPhrase, setSignoffPhrase] = useState('');
  const [signatureHtml, setSignatureHtml] = useState('');
  const [autonomyMode, setAutonomyMode] = useState('draft_and_approve');

  // 2026-02 — Optional uploaded signature image (PNG / JPG / SVG, ≤ 2MB).
  const [signatureImageUrl, setSignatureImageUrl] = useState('');
  const [signatureImageUploading, setSignatureImageUploading] = useState(false);
  const [signatureImageError, setSignatureImageError] = useState('');

  // 2026-02 — AI Assistant persona name. Empty string = respond as agency directly.
  const [aiPersonaName, setAiPersonaName] = useState('');
  const [aiPersonaSaving, setAiPersonaSaving] = useState(false);
  const [aiPersonaBanner, setAiPersonaBanner] = useState(null); // { kind, text }
  
  // Connection status
  const [connectionStatus, setConnectionStatus] = useState('not_connected');
  const [connectionError, setConnectionError] = useState('');
  const [connectionVerifiedAt, setConnectionVerifiedAt] = useState(null);
  
  // Provider detection
  const [detectedProvider, setDetectedProvider] = useState(null);
  // ---- Professional-email-only config (replaces personal OAuth flow) ----
  // `emailProvider === 'auto'` means "use MX-detected provider".
  // `mxDetected` holds the backend's /detect-provider response.
  const [emailProvider, setEmailProvider] = useState('auto');
  const [emailDomainError, setEmailDomainError] = useState('');
  const [mxDetected, setMxDetected] = useState(null);    // { provider, provider_label, auth_type, ... }
  const [mxDetecting, setMxDetecting] = useState(false);
  const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com',
    'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
    'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'aim.com',
    'gmx.com', 'gmx.co.uk', 'gmx.net',
    'protonmail.com', 'proton.me', 'pm.me',
    'mail.com', 'inbox.com', 'fastmail.com', 'fastmail.fm',
    'zoho.com',  // Zoho.com is consumer; Zoho-hosted business domains use their own
    'tutanota.com', 'tutamail.com',
    'btinternet.com', 'sky.com', 'virginmedia.com', 'talktalk.net', 'plus.net',
    'ntlworld.com', 'tiscali.co.uk', 'orange.fr', 'web.de', 't-online.de',
  ];
  const PROVIDER_PRESETS = {
    ionos:    { imap_host: 'imap.ionos.co.uk',     imap_port: 993,
                smtp_host: 'smtp.ionos.co.uk',     smtp_port: 587 },
    // GoDaddy splits into two products. Most modern customers are on Microsoft 365.
    godaddy_m365:      { imap_host: 'outlook.office365.com',  imap_port: 993,
                         smtp_host: 'smtp.office365.com',     smtp_port: 587 },
    godaddy_workspace: { imap_host: 'imap.secureserver.net',  imap_port: 993,
                         smtp_host: 'smtpout.secureserver.net', smtp_port: 465 },
    one23reg: { imap_host: 'mail.123-reg.co.uk',   imap_port: 993,
                smtp_host: 'mail.123-reg.co.uk',   smtp_port: 465 },
    zoho:     { imap_host: 'imap.zoho.eu',         imap_port: 993,
                smtp_host: 'smtp.zoho.eu',         smtp_port: 587 },
    zen:      { imap_host: 'mail.zen.co.uk',       imap_port: 993,
                smtp_host: 'smtp.zen.co.uk',       smtp_port: 587 },
    talktalk: { imap_host: 'mail.talktalk.net',    imap_port: 993,
                smtp_host: 'smtp.talktalk.net',    smtp_port: 587 },
    btconnect:{ imap_host: 'mail.btconnect.com',   imap_port: 993,
                smtp_host: 'smtp.btconnect.com',   smtp_port: 587 },
    // OAuth-only — no IMAP/SMTP presets needed; UI shows a Connect button.
    microsoft365_oauth: null,
    google_workspace:   null,
    other:    null,
  };
  // Effective provider — what we should actually act on after MX detection
  // + manual override. If user has explicitly picked a non-`auto` option,
  // that wins. Otherwise we trust the MX-detected provider.
  const effectiveProvider = (emailProvider && emailProvider !== 'auto')
    ? emailProvider
    : (mxDetected?.provider || null);
  const PROVIDER_LABELS = {
    ionos:             'Ionos',
    godaddy_m365:      'GoDaddy — Microsoft 365',
    godaddy_workspace: 'GoDaddy — Workspace Email (legacy)',
    one23reg:          '123-reg',
    zoho:              'Zoho',
    zen:               'Zen Internet',
    talktalk:          'TalkTalk Business',
    btconnect:         'BT Connect',
    microsoft365_oauth:'Microsoft 365 (OAuth)',
    google_workspace:  'Google Workspace (OAuth)',
    other:             'Other',
  };
  
  // Step-by-step test progress
  const [testSteps, setTestSteps] = useState([]);
  
  // Autonomy mode change dialog
  const [showAutonomyDialog, setShowAutonomyDialog] = useState(false);
  const [pendingAutonomyMode, setPendingAutonomyMode] = useState('');
  
  // WhatsApp settings
  const [whatsappBusinessNumber, setWhatsappBusinessNumber] = useState('');
  const [whatsappApiKey, setWhatsappApiKey] = useState('');
  const [hasWhatsappApiKey, setHasWhatsappApiKey] = useState(false);
  const [whatsappConnectionMode, setWhatsappConnectionMode] = useState('baileys');
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  
  // OpenClaw Integrations settings
  const [openclawApiKeyMasked, setOpenclawApiKeyMasked] = useState('');
  const [openclawApiKeyFull, setOpenclawApiKeyFull] = useState('');
  const [openclawLastUsed, setOpenclawLastUsed] = useState(null);
  const [openclawConnectionStatus, setOpenclawConnectionStatus] = useState('never_connected');
  const [openclawApiUrl, setOpenclawApiUrl] = useState('');
  const [openclawClientId, setOpenclawClientId] = useState('');
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState('');
  
  // Notification settings state
  const [notifDigestEnabled, setNotifDigestEnabled] = useState(true);
  const [notifDigestTime, setNotifDigestTime] = useState('08:00');
  const [notifDigestChannel, setNotifDigestChannel] = useState('email');
  const [notifAlertChannel, setNotifAlertChannel] = useState('email');
  const [notifUrgentChannel, setNotifUrgentChannel] = useState('both');
  const [notifEmail, setNotifEmail] = useState('');
  const [notifWhatsappNumber, setNotifWhatsappNumber] = useState('');
  const [notifSaveMessage, setNotifSaveMessage] = useState(null);
  const [notifPendingReminderHours, setNotifPendingReminderHours] = useState(4);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSendingDigest, setNotifSendingDigest] = useState(false);
  const [notifLoaded, setNotifLoaded] = useState(false);

  // Step 40 — Multi-number agent WhatsApp access (staff numbers)
  const [staffNumbers, setStaffNumbers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  // null = closed; {} or {id, ...} = open in add/edit mode
  const [staffForm, setStaffForm] = useState(null);
  const [staffSaving, setStaffSaving] = useState(false);
  const [staffError, setStaffError] = useState(null);

  // Landlord state
  const [landlords, setLandlords] = useState([]);
  const [landlordForm, setLandlordForm] = useState(null);
  // CHANGE 2 — Show the unified Add-Landlord flow component instead of
  // the legacy form when the agent clicks "+ Add Landlord". Editing an
  // existing landlord still uses the legacy form so all fields remain
  // editable (per spec).
  const [showUnifiedFlow, setShowUnifiedFlow] = useState(false);
  const [landlordSaving, setLandlordSaving] = useState(false);

  // Billing state
  const [billingInfo, setBillingInfo] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Microsoft 365 OAuth state (Step 42 — replaces basic-auth IMAP for M365)
  const [ms365Status, setMs365Status] = useState({ connected: false, configured: false });
  const [ms365Connecting, setMs365Connecting] = useState(false);
  const [ms365Banner, setMs365Banner] = useState(null);

  // Read ?ms=connected|error from the URL once on mount and show a banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ms = params.get('ms');
    const reason = params.get('reason');
    if (ms === 'connected') {
      setMs365Banner({ kind: 'success', text: 'Microsoft 365 connected successfully.' });
      // Clean the URL so refreshes don't re-show the banner.
      const url = new URL(window.location.href);
      url.searchParams.delete('ms');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    } else if (ms === 'error') {
      const map = {
        personal_account_blocked: 'Estatel AI only supports business Microsoft 365 accounts. Personal Outlook/Hotmail accounts are not allowed.',
        state_expired: 'The Microsoft sign-in flow timed out. Please click Connect again.',
        missing_state: 'The Microsoft callback was missing required state. Please try again.',
        exchange_failed: 'Estatel AI could not exchange the Microsoft authorisation code. Please try again.',
      };
      setMs365Banner({
        kind: 'error',
        text: map[reason] || `Microsoft 365 sign-in failed (${reason || 'unknown error'}). Please try again.`,
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('ms');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const fetchMs365Status = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/ms365/status`, {
        headers: { 'Authorization': API_KEY },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setMs365Status(data || { connected: false, configured: false });
    } catch (e) {
      console.error('ms365 status fetch failed:', e);
    }
  };

  const handleMs365Connect = async () => {
    setMs365Connecting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/ms365/init`, {
        headers: { 'Authorization': API_KEY },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Could not start Microsoft 365 sign-in.');
        setMs365Connecting(false);
        return;
      }
      const data = await resp.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (e) {
      console.error('ms365 init failed:', e);
      setMs365Connecting(false);
    }
  };

  // FIX 8 (2026-05-23): standardised disconnect confirmation copy used for
  // both IMAP/SMTP and Microsoft 365 disconnects.
  const DISCONNECT_CONFIRM_MSG = (
    'Are you sure you want to disconnect this email address? '
    + 'Estatel AI will stop sending and receiving emails until you reconnect.'
  );

  const handleDisconnectEmail = async () => {
    if (!window.confirm(DISCONNECT_CONFIRM_MSG)) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY },
      });
      if (!resp.ok) {
        setSaveBanner({ kind: 'error', text: 'Could not disconnect. Please try again.' });
        return;
      }
      // Wipe local state so the connection form appears fresh.
      setEmailAddress('');
      setEmailPassword('');
      setHasPassword(false);
      setImapHost('');
      setSmtpHost('');
      setEmailProvider('auto');
      setMxDetected(null);
      setConnectionStatus('not_connected');
      setConnectionError('');
      setTestSteps([]);
      setMs365Status({ ...ms365Status, connected: false, upn: null, tenant_id: null, connected_at: null });
      setSaveBanner({ kind: 'success', text: 'Email disconnected. You can connect a new email address.' });
      setTimeout(() => setSaveBanner(null), 3000);
    } catch (e) {
      console.error('disconnect failed:', e);
      setSaveBanner({ kind: 'error', text: 'Could not disconnect. Please try again.' });
    }
  };

  const handleMs365Disconnect = async () => {
    if (!window.confirm(DISCONNECT_CONFIRM_MSG)) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/ms365/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY },
      });
      if (resp.ok) {
        setMs365Status({ ...ms365Status, connected: false, upn: null, tenant_id: null, connected_at: null });
        setMs365Banner({ kind: 'success', text: 'Microsoft 365 disconnected.' });
      }
    } catch (e) {
      console.error('ms365 disconnect failed:', e);
    }
  };

  useEffect(() => {
    fetchEmailSettings();
    fetchMs365Status();
    if (activeTab === 'whatsapp') {
      fetchWhatsAppSettings();
      fetchWhatsAppTemplates();
    }
    if (activeTab === 'integrations') {
      fetchOpenClawSettings();
    }
    if (activeTab === 'notifications') {
      setNotifSaveMessage(null);
      fetchNotificationSettings();
      fetchStaffNumbers();
    }
    if (activeTab === 'landlords') {
      fetchLandlords();
    }
    if (activeTab === 'billing') {
      fetchBillingInfo();
    }
  }, [activeTab]);
  
  const fetchEmailSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/settings`, {
        headers: { 'Authorization': API_KEY }
      });
      
      if (!response.ok) throw new Error('Failed to fetch settings');
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      setEmailAddress(data.email_address || '');
      setSenderName(data.email_sender_name || '');
      setImapHost(data.email_imap_host || '');
      setImapPort(data.email_imap_port || 993);
      setSmtpHost(data.email_smtp_host || '');
      setSmtpPort(data.email_smtp_port || 587);
      setEmailTone(data.email_tone || 'professional');
      setSignoffPhrase(data.email_signoff_phrase || '');
      setSignatureHtml(data.email_signature_html || '');
      setSignatureImageUrl(data.email_signature_image_url || '');
      setAiPersonaName(data.ai_persona_name || '');
      setAutonomyMode(data.email_autonomy_mode || 'draft_and_approve');
      setHasPassword(data.has_password || false);
      setConnectionVerifiedAt(data.email_connection_verified_at || null);
      
      if (data.email_connection_status === 'connected') {
        setConnectionStatus('connected');
      } else if (data.email_address && data.email_imap_host && data.has_password) {
        setConnectionStatus('connected');
      }
      
      // Run MX-based detection so the UI can show the detected-provider
      // banner and pre-fill the dropdown when the agent revisits Settings.
      if (data.email_address) {
        // Defer until the helper is defined below; using setTimeout(0) keeps
        // this side-effect out of the synchronous render path.
        setTimeout(() => runMxDetection(data.email_address), 0);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      setLoading(false);
    }
  };

  // Backend MX-lookup. Runs on email blur. Personal-domain errors are
  // surfaced as `emailDomainError`; everything else populates `mxDetected`
  // so the UI can pre-fill servers / show the OAuth button.
  const runMxDetection = async (email) => {
    if (!email || !email.includes('@')) return;
    const err = validateProfessionalDomain(email);
    if (err) { setMxDetected(null); return; }
    setMxDetecting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/detect-provider`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_address: email }),
      });
      if (!resp.ok) {
        // 400 = personal domain — already surfaced via emailDomainError
        setMxDetected(null);
        return;
      }
      const data = await resp.json();
      setMxDetected(data);
      // If user is still on the "Auto-detected" option, apply the preset
      // automatically (IMAP/SMTP password providers only — OAuth providers
      // render their own Connect button).
      if (emailProvider === 'auto' && data?.provider && data?.auth_type === 'password') {
        setImapHost(data.imap_host || '');
        setImapPort(data.imap_port || 993);
        setSmtpHost(data.smtp_host || '');
        setSmtpPort(data.smtp_port || 587);
      }
    } catch (e) {
      console.error('MX detect failed:', e);
      setMxDetected(null);
    } finally {
      setMxDetecting(false);
    }
  };

  const validateProfessionalDomain = (email) => {
    if (!email) return '';
    if (!email.includes('@')) {
      return 'Enter a complete email address (e.g. you@yourcompany.co.uk).';
    }
    const domain = email.split('@')[1]?.toLowerCase().trim();
    if (!domain) {
      return 'Enter a complete email address (e.g. you@yourcompany.co.uk).';
    }
    if (PERSONAL_EMAIL_DOMAINS.includes(domain)) {
      return ('Estatel AI only supports professional email addresses. Please '
              + 'use your business email address.');
    }
    return '';
  };

  const handleProviderChange = (val) => {
    // 'auto' is special — fall back to the MX-detected provider (no manual override).
    setEmailProvider(val);
    if (val === 'auto') {
      // Re-apply MX-detected preset (if any)
      if (mxDetected?.auth_type === 'password' && mxDetected?.provider) {
        setImapHost(mxDetected.imap_host || '');
        setImapPort(mxDetected.imap_port || 993);
        setSmtpHost(mxDetected.smtp_host || '');
        setSmtpPort(mxDetected.smtp_port || 587);
      }
      setConnectionStatus('not_connected');
      setConnectionError('');
      setTestSteps([]);
      return;
    }
    const preset = PROVIDER_PRESETS[val];
    if (preset) {
      setImapHost(preset.imap_host);
      setImapPort(preset.imap_port);
      setSmtpHost(preset.smtp_host);
      setSmtpPort(preset.smtp_port);
    } else if (val === 'other') {
      setImapHost(''); setImapPort(993);
      setSmtpHost(''); setSmtpPort(587);
    } else {
      // OAuth providers (microsoft365_oauth / google_workspace) — clear
      // IMAP/SMTP since they're not used.
      setImapHost(''); setImapPort(993);
      setSmtpHost(''); setSmtpPort(587);
    }
    setConnectionStatus('not_connected');
    setConnectionError('');
    setTestSteps([]);
  };

  const handleEmailAddressChange = (val) => {
    setEmailAddress(val);
    setEmailDomainError(validateProfessionalDomain(val));
    if (val !== emailAddress) {
      setConnectionStatus('not_connected');
      setConnectionError('');
      setTestSteps([]);
      // MX detection waits for blur — clear stale detection if the address
      // is changing.
      setMxDetected(null);
    }
  };
  
  const handleAutoDetect = async () => {
    if (!emailAddress) return;
    
    setTestingConnection(true);
    setConnectionError('');
    setImapHost('');
    setImapPort(993);
    setSmtpHost('');
    setSmtpPort(587);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/setup`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_address: emailAddress, password: emailPassword || null })
      });
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      if (data.success) {
        setImapHost(data.imap_host);
        setImapPort(data.imap_port);
        setSmtpHost(data.smtp_host);
        setSmtpPort(data.smtp_port);
      } else {
        setConnectionError(data.error || 'Auto-detection failed');
      }
    } catch (error) {
      setConnectionError(error.message);
    } finally {
      setTestingConnection(false);
    }
  };
  
  const handleTestConnection = async () => {
    if (!emailAddress || !emailPassword || !imapHost || !smtpHost) {
      alert('Please fill in all connection fields');
      return;
    }
    
    setTestingConnection(true);
    setConnectionError('');
    setTestSteps([
      { step: 'imap_connect', label: 'Connecting to IMAP server...', status: 'running' },
    ]);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_address: emailAddress,
          password: emailPassword,
          imap_host: imapHost,
          imap_port: parseInt(imapPort),
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort),
          provider: effectiveProvider || undefined,
        })
      });
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      setTestSteps(data.steps || []);
      
      if (data.success) {
        setConnectionStatus('connected');
        setConnectionError('');
      } else {
        setConnectionStatus('error');
        const failedStep = (data.steps || []).find(s => s.status === 'fail');
        setConnectionError(failedStep?.error || 'Connection test failed.');
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionError(error.message);
      setTestSteps([{ step: 'error', label: 'Connection failed', status: 'fail', error: error.message }]);
    } finally {
      setTestingConnection(false);
    }
  };
  
  const [saveBanner, setSaveBanner] = useState(null); // FIX 9: { kind, text }

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveBanner(null);
    // FIX 10 (2026-05-23): normalise newlines in the signature to <br>
    // BEFORE save so the stored value emails out with the formatting the
    // agent saw in the live preview. If the agent already typed `<br>`
    // they're respected — only bare \n line breaks are converted.
    const normalisedSignature = (signatureHtml || '').replace(/(?<!>)\n/g, '<br>');
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/preferences`, {
        method: 'PUT',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_address: emailAddress,
          email_sender_name: senderName,
          email_imap_host: imapHost,
          email_imap_port: parseInt(imapPort),
          email_smtp_host: smtpHost,
          email_smtp_port: parseInt(smtpPort),
          email_password: emailPassword || undefined,
          email_tone: emailTone,
          email_signoff_phrase: signoffPhrase,
          email_signature_html: normalisedSignature,
          email_autonomy_mode: autonomyMode
        })
      });
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      if (response.ok && data.success) {
        setEmailPassword('');
        setHasPassword(true);
        await fetchEmailSettings();
        // FIX 9 (2026-05-23): clear confirmation banner (was silent before).
        setSaveBanner({ kind: 'success', text: 'Settings saved successfully.' });
        setTimeout(() => setSaveBanner(null), 3000);
      } else {
        setSaveBanner({ kind: 'error', text: 'Settings could not be saved. Please try again.' });
      }
    } catch (error) {
      setSaveBanner({
        kind: 'error',
        text: `Settings could not be saved. Please try again. (${error.message})`,
      });
    } finally {
      setSaving(false);
    }
  };
  // 2026-02 — One-click Test Send via the Resend transactional path
  // (the same code path as signup-verification + password-reset emails).
  // Does NOT touch the existing handleSendTestEmail above which tests
  // the agent's own SMTP / MS365 outbound. This one verifies that
  // system emails (from noreply@estatel.ai) can reach the agent's
  // contact email. Result is rendered inline as a green or red panel.
  const handleTransactionalTestSend = async () => {
    setTransactionalResult(null);
    setTransactionalTesting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/send-transactional-test`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
      });
      let data = {};
      try { data = await resp.clone().json(); }
      catch { try { data = JSON.parse(await resp.clone().text()); } catch { data = {}; } }
      if (resp.ok && data && data.delivered) {
        setTransactionalResult({
          ok: true,
          channel: data.channel || 'unknown',
          message_id: data.message_id || null,
          to: data.to || '',
          error: null,
        });
      } else {
        setTransactionalResult({
          ok: false,
          channel: (data && data.channel) || null,
          message_id: null,
          to: (data && data.to) || '',
          error: (data && (data.error || data.detail)) || `Request failed (${resp.status})`,
        });
      }
    } catch (err) {
      setTransactionalResult({
        ok: false,
        channel: null,
        message_id: null,
        to: '',
        error: err && err.message ? err.message : 'Network error',
      });
    } finally {
      setTransactionalTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!emailAddress) { alert('Please configure email settings first'); return; }
    setSendingTest(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/send-test`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: emailAddress })
      });
      // Defensive: the Emergent preview wrapper sometimes consumes the
      // response body before we read it. Clone before reading.
      let data = {};
      try {
        data = await response.clone().json();
      } catch (e1) {
        try { data = JSON.parse(await response.clone().text()); } catch (e2) { data = {}; }
      }
      if (response.ok && data.success) {
        alert('Test email sent! Check your inbox.');
      } else {
        alert(data.detail || data.message || data.error || 'Failed to send test email. Check your email settings.');
      }
    } catch (error) {
      // The wrapper's DataCloneError on a 200 response is a false positive
      // — the email almost certainly went out. Tell the agent to verify
      // their inbox before assuming failure.
      if (String(error.message || '').includes('Request object could not be cloned')
          || String(error.message || '').includes('body stream already read')) {
        alert('Sent. (The preview environment intercepted the response, so verification was skipped — please check your inbox to confirm delivery.)');
      } else {
        alert('Error: ' + error.message);
      }
    } finally {
      setSendingTest(false);
    }
  };

  // 2026-02 — Upload a signature image (PNG / JPG / SVG, max 2MB).
  // Server saves the bytes and returns the public URL to embed in emails.
  const handleSignatureImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-uploading the same filename later
    if (!file) return;
    setSignatureImageError('');
    const okTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!okTypes.includes((file.type || '').toLowerCase())) {
      setSignatureImageError('Please upload a PNG, JPG, or SVG file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setSignatureImageError('File too large. Maximum 2 MB.');
      return;
    }
    setSignatureImageUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${BACKEND_URL}/api/email/signature-image/upload`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY },
        body: form,
      });
      const txt = await resp.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!resp.ok) {
        setSignatureImageError(data.detail || 'Upload failed.');
        return;
      }
      setSignatureImageUrl(data.email_signature_image_url || '');
    } catch (err) {
      setSignatureImageError('Upload failed: ' + err.message);
    } finally {
      setSignatureImageUploading(false);
    }
  };

  const handleSignatureImageRemove = async () => {
    setSignatureImageError('');
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/signature-image`, {
        method: 'DELETE',
        headers: { 'Authorization': API_KEY },
      });
      if (resp.ok) {
        setSignatureImageUrl('');
      } else {
        const txt = await resp.text();
        const data = txt ? JSON.parse(txt) : {};
        setSignatureImageError(data.detail || 'Failed to remove image.');
      }
    } catch (err) {
      setSignatureImageError('Failed to remove image: ' + err.message);
    }
  };

  // 2026-02 — Save just the AI persona name. The full email settings save
  // also updates this field, but the dedicated AI Assistant tab uses this
  // lightweight handler so the agent gets a tight save -> confirm loop.
  const handleSaveAiPersona = async () => {
    setAiPersonaSaving(true);
    setAiPersonaBanner(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/email/preferences`, {
        method: 'PUT',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_persona_name: aiPersonaName || '' }),
      });
      const txt = await resp.text();
      const data = txt ? JSON.parse(txt) : {};
      if (resp.ok && data.success) {
        setAiPersonaBanner({ kind: 'success', text: 'AI Assistant name saved.' });
        setTimeout(() => setAiPersonaBanner(null), 3000);
      } else {
        setAiPersonaBanner({ kind: 'error', text: data.detail || 'Save failed.' });
      }
    } catch (err) {
      setAiPersonaBanner({ kind: 'error', text: 'Save failed: ' + err.message });
    } finally {
      setAiPersonaSaving(false);
    }
  };

  const handleClearAiPersona = () => {
    setAiPersonaName('');
  };

  
  const handleAutonomyModeChange = (newMode) => {
    if (newMode === 'full_autonomy') {
      // Show confirmation dialog
      setPendingAutonomyMode(newMode);
      setShowAutonomyDialog(true);
    } else {
      setAutonomyMode(newMode);
    }
  };
  
  const confirmAutonomyModeChange = () => {
    setAutonomyMode(pendingAutonomyMode);
    setShowAutonomyDialog(false);
    setPendingAutonomyMode('');
  };
  
  const cancelAutonomyModeChange = () => {
    setShowAutonomyDialog(false);
    setPendingAutonomyMode('');
  };
  
  // ============================================================================
  // WHATSAPP FUNCTIONS
  // ============================================================================
  
  const fetchWhatsAppSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/settings`, {
        headers: { 'Authorization': API_KEY }
      });
      
      if (!response.ok) throw new Error('Failed to fetch WhatsApp settings');
      
      const data = await response.json();
      
      setWhatsappBusinessNumber(data.whatsapp_business_number || '');
      setHasWhatsappApiKey(data.has_api_key || false);
      setWhatsappConnectionMode(data.connection_mode || 'baileys');
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    }
  };
  
  const fetchWhatsAppTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates`, {
        headers: { 'Authorization': API_KEY }
      });
      
      if (!response.ok) throw new Error('Failed to fetch templates');
      
      const data = await response.json();
      setWhatsappTemplates(data);
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
    }
  };
  
  const handleSaveWhatsAppSettings = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          whatsapp_business_number: whatsappBusinessNumber,
          // Send empty string explicitly to clear the API key (switch to Baileys mode)
          // Send actual value if provided, or empty string if cleared
          whatsapp_360dialog_api_key: whatsappApiKey
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ WhatsApp settings saved successfully!');
        // Clear password field after saving
        setWhatsappApiKey('');
        // Refresh settings from database to update connection mode banner
        await fetchWhatsAppSettings();
      } else {
        alert('Failed to save WhatsApp settings');
      }
    } catch (error) {
      alert('Error saving WhatsApp settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSyncTemplates = async () => {
    setSyncingTemplates(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates/sync`, {
        method: 'POST',
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ ${data.message}`);
        await fetchWhatsAppTemplates();
      } else {
        alert('Failed to sync templates');
      }
    } catch (error) {
      alert('Error syncing templates: ' + error.message);
    } finally {
      setSyncingTemplates(false);
    }
  };

  // ============================================================================
  // OPENCLAW INTEGRATIONS FUNCTIONS
  // ============================================================================
  
  const fetchOpenClawSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/openclaw/settings`, {
        headers: { 'Authorization': API_KEY }
      });
      
      if (!response.ok) throw new Error('Failed to fetch OpenClaw settings');
      
      const data = await response.json();
      
      setOpenclawApiKeyMasked(data.api_key_masked || '');
      setOpenclawLastUsed(data.last_used);
      setOpenclawConnectionStatus(data.connection_status);
      setOpenclawApiUrl(data.api_url);
      setOpenclawClientId(data.agent_client_id);
    } catch (error) {
      console.error('Error fetching OpenClaw settings:', error);
    }
  };
  
  const handleCopyApiKey = async () => {
    // Copy OpenClaw API key to clipboard - v2 fixed
    try {
      if (openclawApiKeyFull) {
        await navigator.clipboard.writeText(openclawApiKeyFull);
        alert('✅ API key copied to clipboard');
      } else {
        alert('⚠️ Full API key not available. Please regenerate to get the full key, or check the database.');
      }
    } catch (error) {
      alert('Error copying API key: ' + error.message);
    }
  };
  
  const handleRegenerateKey = async () => {
    setRegeneratingKey(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/openclaw/regenerate-key`, {
        method: 'POST',
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Store the full key temporarily for copying
        setOpenclawApiKeyFull(data.api_key_full);
        setOpenclawApiKeyMasked(data.api_key_masked);
        setOpenclawConnectionStatus('never_connected');
        setOpenclawLastUsed(null);
        setNewlyGeneratedKey(data.api_key_full);
        
        // Show new key dialog with copy button
        setShowRegenerateDialog(false);
        setShowNewKeyDialog(true);
      } else {
        alert('Failed to regenerate API key');
      }
    } catch (error) {
      alert('Error regenerating API key: ' + error.message);
    } finally {
      setRegeneratingKey(false);
    }
  };
  
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ Copied to clipboard');
    } catch (error) {
      alert('Error copying to clipboard: ' + error.message);
    }
  };
  
  const handleCopyNewKey = async () => {
    try {
      await navigator.clipboard.writeText(newlyGeneratedKey);
      alert('✅ API key copied to clipboard successfully!');
    } catch (error) {
      console.error('Clipboard write failed:', error);
      alert('⚠️ Auto-copy failed. Please manually copy the key above.');
    }
  };

  // ============================================================================
  // NOTIFICATION SETTINGS FUNCTIONS
  // ============================================================================

  const fetchNotificationSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
        headers: { 'Authorization': API_KEY }
      });
      if (!response.ok) throw new Error('Failed to fetch notification settings');
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      setNotifDigestEnabled(data.digest_enabled !== false);
      setNotifDigestTime(data.digest_time || '08:00');
      setNotifDigestChannel(data.digest_channel || 'email');
      setNotifAlertChannel(data.notification_channel || 'email');
      setNotifUrgentChannel(data.urgent_alert_channel || 'both');
      setNotifEmail(data.notification_email || '');
      setNotifWhatsappNumber(data.notification_whatsapp_number || '');
      setNotifPendingReminderHours(data.pending_reminder_hours || 4);
      setNotifLoaded(true);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setNotifSaving(true);
    setNotifSaveMessage(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          digest_enabled: notifDigestEnabled,
          digest_time: notifDigestTime,
          digest_channel: notifDigestChannel,
          notification_channel: notifAlertChannel,
          notification_email: notifEmail,
          notification_whatsapp_number: notifWhatsappNumber,
          urgent_alert_channel: notifUrgentChannel,
          pending_reminder_hours: notifPendingReminderHours,
        })
      });
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      if (response.ok && data.success) {
        setNotifSaveMessage({ ok: true, text: 'Settings saved successfully.' });
        // Re-fetch so the inputs reflect what's actually persisted (e.g. if
        // the backend trims/normalises values).
        await fetchNotificationSettings();
      } else {
        setNotifSaveMessage({ ok: false, text: data.detail || 'Failed to save notification settings.' });
      }
    } catch (error) {
      setNotifSaveMessage({ ok: false, text: 'Error saving notification settings: ' + error.message });
    } finally {
      setNotifSaving(false);
    }
  };

  const handleSendTestDigest = async () => {
    setNotifSendingDigest(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/send-test-digest`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY }
      });
      const data = await response.json();
      if (data.channels_sent && data.channels_sent.length > 0) {
        alert(`Test digest sent via: ${data.channels_sent.join(', ')}. Check your inbox or WhatsApp.`);
      } else if (data.skipped) {
        alert('Digest is disabled. Enable the daily digest toggle first.');
      } else {
        alert('Digest was generated but no channels were configured to receive it. Please set an email or WhatsApp number.');
      }
    } catch (error) {
      alert('Error sending test digest: ' + error.message);
    } finally {
      setNotifSendingDigest(false);
    }
  };

  // ============================================================================
  // STAFF WHATSAPP ACCESS (Step 40)
  // ============================================================================
  const EMPTY_STAFF = { display_name: '', phone_number: '', role: 'standard' };

  const fetchStaffNumbers = async () => {
    setStaffLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-numbers`, {
        headers: { 'Authorization': API_KEY },
      });
      if (res.ok) {
        const txt = await res.text();
        setStaffNumbers(txt ? JSON.parse(txt) : []);
      }
    } catch (err) {
      console.error('Error fetching staff numbers:', err);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleSaveStaff = async () => {
    if (!staffForm) return;
    setStaffError(null);
    if (!staffForm.display_name || !staffForm.display_name.trim()) {
      setStaffError('Display name is required.');
      return;
    }
    if (!staffForm.id) {
      const raw = (staffForm.phone_number || '').trim();
      if (!raw) {
        setStaffError('Phone number is required.');
        return;
      }
      // Client-side UK mobile sanity check — must normalise to +447XXXXXXXXX (13 chars).
      // Mirrors backend `_is_valid_uk_mobile` so the agent gets instant feedback
      // before a server round-trip.
      const digits = raw.replace(/[^0-9+]/g, '');
      let e164 = '';
      if (digits.startsWith('+44')) e164 = digits;
      else if (digits.startsWith('0044')) e164 = '+' + digits.slice(2);
      else if (digits.startsWith('44') && digits.length >= 12) e164 = '+' + digits;
      else if (digits.startsWith('0')) e164 = '+44' + digits.slice(1);
      else if (digits) e164 = '+44' + digits;
      const validUkMobile = /^\+447\d{9}$/.test(e164);
      if (!validUkMobile) {
        setStaffError('Phone number is not a valid UK mobile (e.g. 07911 123 456 or +447911123456).');
        return;
      }
    }
    setStaffSaving(true);
    try {
      const isEdit = !!staffForm.id;
      const url = isEdit
        ? `${BACKEND_URL}/api/staff-numbers/${staffForm.id}`
        : `${BACKEND_URL}/api/staff-numbers`;
      const body = isEdit
        ? { display_name: staffForm.display_name.trim(), role: staffForm.role }
        : {
            display_name: staffForm.display_name.trim(),
            phone_number: staffForm.phone_number.trim(),
            role: staffForm.role,
          };
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setStaffError(data.detail || 'Failed to save staff number.');
        return;
      }
      setStaffForm(null);
      await fetchStaffNumbers();
    } catch (err) {
      setStaffError('Network error: ' + err.message);
    } finally {
      setStaffSaving(false);
    }
  };

  const handleDeactivateStaff = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-numbers/${id}/deactivate`, {
        method: 'POST', headers: { 'Authorization': API_KEY },
      });
      if (res.ok) await fetchStaffNumbers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleReactivateStaff = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-numbers/${id}/reactivate`, {
        method: 'POST', headers: { 'Authorization': API_KEY },
      });
      if (res.ok) await fetchStaffNumbers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleRemoveStaff = async (id, displayName) => {
    if (!window.confirm(`Permanently remove ${displayName}'s WhatsApp access? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/staff-numbers/${id}`, {
        method: 'DELETE', headers: { 'Authorization': API_KEY },
      });
      if (res.ok) await fetchStaffNumbers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // ============================================================================
  // LANDLORD FUNCTIONS
  // ============================================================================

  const EMPTY_LANDLORD = {
    full_name: '', company_name: '', address_line1: '', address_line2: '',
    city: '', postcode: '', email: '', phone_number: '', prs_database_registered: false,
    // 2026-05-06 (Items 1+2) — landlord type drives statement-of-truth
    // signing on N5 / N119 / N215 and the §5.4 name on Form 3A.
    landlord_type: 'individual',
    director_name: '',
    // Step 53 BUG 5 — let property address (separate from landlord's correspondence
    // address). When provided on Add Landlord, Estatel AI creates a Property
    // record linked to this landlord automatically.
    let_address_line1: '', let_address_line2: '', let_city: '', let_postcode: '',
  };

  const fetchLandlords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landlords`, {
        headers: { 'Authorization': API_KEY }
      });
      if (res.ok) setLandlords(await res.json());
    } catch (err) {
      console.error('Error fetching landlords:', err);
    }
  };

  const handleSaveLandlord = async () => {
    if (!landlordForm.full_name || !landlordForm.address_line1 || !landlordForm.city || !landlordForm.postcode || !landlordForm.email || !landlordForm.phone_number) {
      alert('Please fill in all required fields: Full Name, Address Line 1, City, Postcode, Email, Phone Number');
      return;
    }
    // 2026-05-06 (Items 1+2) — when the agent picks "Limited company"
    // both company_name and director_name become required. The director
    // signs the Statement of Truth on every court document.
    if ((landlordForm.landlord_type || 'individual') === 'company') {
      if (!String(landlordForm.company_name || '').trim()) {
        alert('Company name is required for Limited company landlords.');
        return;
      }
      if (!String(landlordForm.director_name || '').trim()) {
        alert('Director name is required for Limited company landlords. The director signs court documents on the company\'s behalf.');
        return;
      }
    }
    setLandlordSaving(true);
    try {
      const isEdit = !!landlordForm._id;
      const url = isEdit ? `${BACKEND_URL}/api/landlords/${landlordForm._id}` : `${BACKEND_URL}/api/landlords`;
      const method = isEdit ? 'PUT' : 'POST';
      // Step 53 BUG 5 — strip let_* fields from the landlord payload; they get
      // sent separately to /api/properties below.
      const { let_address_line1, let_address_line2, let_city, let_postcode, ...landlordPayload } = landlordForm;
      const res = await fetch(url, {
        method, headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify(landlordPayload)
      });
      if (res.ok) {
        const saved = await res.json();
        const landlordId = saved._id || landlordForm._id;
        // If the agent has provided a let property address, create a Property
        // record linked to this landlord. Only on the create path; on edit
        // we let the agent manage Let Properties from the existing list.
        if (!isEdit && let_address_line1 && let_city && let_postcode && landlordId) {
          try {
            await fetch(`${BACKEND_URL}/api/properties`, {
              method: 'POST',
              headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                address_line1: let_address_line1,
                address_line2: let_address_line2 || '',
                city: let_city,
                postcode: let_postcode,
                landlord_id: landlordId,
              }),
            });
          } catch (e) {
            // Surface but don't roll back the landlord — agent can attach later.
            alert('Landlord saved, but failed to create the let property: ' + (e.message || e));
          }
        }
        setLandlordForm(null);
        fetchLandlords();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save landlord');
      }
    } catch (err) {
      alert('Error saving landlord: ' + err.message);
    } finally {
      setLandlordSaving(false);
    }
  };

  const handleDeleteLandlord = async (id, name, propCount) => {
    if (propCount > 0) {
      alert(`Cannot delete ${name} — ${propCount} properties are linked. Unlink properties first.`);
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/landlords/${id}`, {
        method: 'DELETE', headers: { 'Authorization': API_KEY }
      });
      if (res.ok) fetchLandlords();
      else {
        const err = await res.json();
        alert(err.detail || 'Failed to delete landlord');
      }
    } catch (err) {
      alert('Error deleting landlord: ' + err.message);
    }
  };

  // Billing functions
  const fetchBillingInfo = async () => {
    setBillingLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/billing`, { headers: { 'Authorization': API_KEY } });
      if (res.ok) {
        const text = await res.text();
        setBillingInfo(text ? JSON.parse(text) : null);
      }
    } catch {} finally { setBillingLoading(false); }
  };

  const handleManageBilling = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/portal`, {
        method: 'POST', headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (res.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        alert(data.detail || 'Unable to open billing portal');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }
  
  return (
    <div className="settings-container">
      <h1 className="settings-title">Settings</h1>
      
      {/* Tabs — hidden when the Settings component is mounted as the
          standalone Landlords page (via App.js currentView='landlords').
          The landlord-specific block still renders because activeTab is
          forced to 'landlords' via initialTab. */}
      {!hideTabs && (
      <div className="settings-tabs">
        {/* Onboarding entry — moved here from the sidebar (2026-06-01).
            Clicking does NOT switch tab — it opens the full-screen
            wizard via the onOpenOnboarding callback supplied by App.js.
            That way concierge-onboarded agents who have no need for
            self-serve setup can still launch the wizard from one
            place if they want. */}
        {onOpenOnboarding && (
          <button
            className="settings-tab"
            onClick={onOpenOnboarding}
            data-testid="tab-onboarding"
          >
            Onboarding
          </button>
        )}
        {/* 2026-05-06 (Item 1) — Agency Details tab. Position: FIRST tab.
            Allows the agent to view/edit the AgentClient correspondence
            address used by Form 3A §5.5 plus the agency contact fields. */}
        <button
          className={`settings-tab ${activeTab === 'agency_details' ? 'active' : ''}`}
          onClick={() => setActiveTab('agency_details')}
          data-testid="tab-agency-details"
        >
          Agency Details
        </button>
        <button 
          className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
          data-testid="tab-email"
        >
          Email Integration
        </button>
        {WHATSAPP_ENABLED && (
          <button 
            className={`settings-tab ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp')}
            data-testid="tab-whatsapp"
          >
            WhatsApp
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'ai_assistant' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai_assistant')}
          data-testid="tab-ai-assistant"
        >
          AI Assistant
        </button>
        <button 
          className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
          data-testid="tab-notifications"
        >
          Notifications
        </button>
        {/* 2026-05-30 — Integrations tab (ClawHosters/OpenClaw) hidden
            per user request. State + endpoints kept for backwards
            compatibility but the tab + Landlords tab are no longer
            navigable. Landlords are now reached from the dashboard
            sidebar Landlords entry. */}
        <button
          className={`settings-tab ${activeTab === 'pre_tenancy' ? 'active' : ''}`}
          onClick={() => setActiveTab('pre_tenancy')}
          data-testid="tab-pre-tenancy"
        >
          Pre-Tenancy
        </button>
        {/* 2026-06-04 — Maintenance settings tab (Part 3). */}
        <button
          className={`settings-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
          data-testid="tab-maintenance"
        >
          Maintenance
        </button>
        <button 
          className={`settings-tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
          data-testid="tab-billing"
        >
          Billing
        </button>
        <button 
          className={`settings-tab ${activeTab === 'data_management' ? 'active' : ''}`}
          onClick={() => setActiveTab('data_management')}
          data-testid="tab-data-management"
        >
          Data Management
        </button>
        <button 
          className={`settings-tab ${activeTab === 'business_contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('business_contacts')}
          data-testid="tab-business-contacts"
        >
          Business Contacts
        </button>
      </div>
      )}
      
      {/* Agency Details Tab — Item 1 (2026-05-06) */}
      {activeTab === 'agency_details' && (
        <AgencyDetailsTab />
      )}

      {/* 2026-02 FIX 3 — Data Management Tab (Archived records). */}
      {activeTab === 'data_management' && (
        <div className="settings-content">
          <ArchivedRecordsPanel />
        </div>
      )}

      {/* 2026-02 — Business Contacts whitelist. */}
      {activeTab === 'business_contacts' && (
        <div className="settings-content">
          <BusinessContactsPanel />
        </div>
      )}

      {/* Email Integration Tab */}
      {activeTab === 'email' && (
        <div className="settings-content">

          {/* FIX 9 (2026-05-23): Save Settings confirmation banner */}
          {saveBanner && (
            <div
              className={`status-banner ${saveBanner.kind === 'success' ? 'status-connected' : 'status-error'}`}
              style={{ marginBottom: '0.75rem' }}
              data-testid={`save-banner-${saveBanner.kind}`}
            >
              {saveBanner.kind === 'success' ? '✅ ' : '❌ '}{saveBanner.text}
            </div>
          )}

          {/* Connection Status Banner */}
          {connectionStatus === 'connected' && (
            <div className="status-banner status-connected" data-testid="status-connected">
              ✅ Email connection active
            </div>
          )}
          {connectionStatus === 'error' && (
            <div className="status-banner status-error" data-testid="status-error">
              ❌ Email connection error: {connectionError}
            </div>
          )}
          {connectionStatus === 'not_connected' && (
            <div className="status-banner status-not-connected" data-testid="status-not-connected">
              ⚠️ Email not connected
            </div>
          )}

          {/* 2026-02 — Resend transactional Test Send.
              Verifies the *system* email channel (signup verification,
              password-reset, etc.) — independent of the agent's
              connected mailbox. Sends a one-line test email from
              noreply@estatel.ai to the agent's contact email. */}
          <div className="settings-section" data-testid="transactional-test-section"
               style={{ marginBottom: '1rem' }}>
            <h2>System email delivery</h2>
            <p style={{ color: '#676879', fontSize: '0.9rem', marginTop: '-0.25rem' }}>
              Send a one-click test from <code>noreply@estatel.ai</code> to your account email
              to confirm system emails (signup verification, password reset, alerts) will reach you.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={handleTransactionalTestSend}
              disabled={transactionalTesting}
              data-testid="btn-transactional-test-send"
              style={{ marginTop: '0.5rem' }}
            >
              {transactionalTesting ? 'Sending test…' : 'Send Test Email'}
            </button>
            {transactionalResult && transactionalResult.ok && (
              <div
                data-testid="transactional-test-success"
                style={{
                  background: '#dcfce7', border: '1px solid #86efac', color: '#14532d',
                  padding: '0.75rem 0.85rem', borderRadius: 6, marginTop: '0.75rem',
                  fontSize: '0.9rem', lineHeight: 1.45,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ✅ Test email delivered to {transactionalResult.to}
                </div>
                <div>Channel: <strong>{transactionalResult.channel}</strong></div>
                {transactionalResult.message_id && (
                  <div style={{ wordBreak: 'break-all' }}>
                    Message ID: <code>{transactionalResult.message_id}</code>
                  </div>
                )}
                <div style={{ marginTop: 4, color: '#166534' }}>
                  Check your inbox to confirm it arrived.
                </div>
              </div>
            )}
            {transactionalResult && !transactionalResult.ok && (
              <div
                data-testid="transactional-test-error"
                style={{
                  background: '#fee2e2', border: '1px solid #fca5a5', color: '#7f1d1d',
                  padding: '0.75rem 0.85rem', borderRadius: 6, marginTop: '0.75rem',
                  fontSize: '0.9rem', lineHeight: 1.45,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ❌ Test email failed to deliver
                </div>
                {transactionalResult.channel && (
                  <div>Channel attempted: <strong>{transactionalResult.channel}</strong></div>
                )}
                {transactionalResult.error && (
                  <div style={{ wordBreak: 'break-word' }}>
                    Error: {transactionalResult.error}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Professional Email Connection */}
          <div className="settings-section">
            <h2>Email Connection</h2>
            <p className="settings-help">
              Estatel AI only connects to <strong>professional email addresses</strong>{' '}
              (business mailboxes hosted by Ionos, GoDaddy, 123-reg, Zoho or
              another business provider). Personal accounts such as Gmail,
              Outlook, Hotmail, Yahoo and iCloud are not supported.
            </p>

            {/* Persistent Connection Status Badge */}
            {connectionStatus === 'connected' && (
              <div className="email-conn-badge email-conn-green" data-testid="email-conn-badge">
                <span className="ecb-dot"></span>
                Connected — {emailAddress}
                {connectionVerifiedAt && <span className="ecb-time">Verified: {formatDate(connectionVerifiedAt, '')}</span>}
              </div>
            )}
            {connectionStatus === 'error' && (
              <div className="email-conn-badge email-conn-red" data-testid="email-conn-badge-error">
                <span className="ecb-dot ecb-dot-red"></span>
                Disconnected
              </div>
            )}

            <div className="form-group">
              <label>Email address *</label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => handleEmailAddressChange(e.target.value)}
                onBlur={(e) => runMxDetection(e.target.value)}
                placeholder="you@yourcompany.co.uk"
                data-testid="input-email-address"
              />
              {emailDomainError && (
                <small className="form-error" data-testid="email-domain-error">
                  {emailDomainError}
                </small>
              )}
              {mxDetecting && (
                <small data-testid="mx-detecting">Looking up mail server settings…</small>
              )}
              {mxDetected && mxDetected.detected && (
                <small className="form-hint" data-testid="mx-detected-banner">
                  We detected your email is hosted by <strong>{mxDetected.provider_label}</strong>.
                  {mxDetected.auth_type === 'password' && ' We have pre-filled the connection settings for you.'}
                  {mxDetected.auth_type === 'oauth' && mxDetected.oauth_provider === 'microsoft'
                    && ' Click "Connect Microsoft 365" below to sign in.'}
                  {mxDetected.auth_type === 'oauth' && mxDetected.oauth_provider === 'google'
                    && !mxDetected.google_oauth_available
                    && ' Google Workspace connection coming soon.'}
                  {mxDetected.auth_type === 'oauth' && mxDetected.oauth_provider === 'google'
                    && mxDetected.google_oauth_available
                    && ' Click "Connect Google" below to sign in.'}
                </small>
              )}
              {mxDetected && !mxDetected.detected && (
                <small className="form-hint" data-testid="mx-not-detected"
                       style={{ display: 'block',
                                 background: '#fef3c7',
                                 color: '#78350f',
                                 padding: '0.55rem 0.8rem',
                                 borderRadius: 6,
                                 border: '1px solid #fde68a',
                                 marginTop: '0.4rem' }}>
                  We could not automatically detect your email provider.
                  Please select it from the dropdown below.
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Email provider</label>
              <div style={{ display: 'flex', gap: '0.5rem',
                             alignItems: 'stretch' }}>
              <select
                value={emailProvider}
                onChange={(e) => handleProviderChange(e.target.value)}
                data-testid="select-email-provider"
                style={{ flex: 1 }}
              >
                <option value="auto">
                  {mxDetected?.provider_label
                    ? `Auto-detected (${mxDetected.provider_label})`
                    : 'Auto-detected'}
                </option>
                <option disabled>──────────</option>
                <option value="microsoft365_oauth">Microsoft 365</option>
                <option value="ionos">Ionos</option>
                <option value="godaddy_workspace">GoDaddy — Workspace Email</option>
                <option value="godaddy_m365">GoDaddy — Microsoft 365</option>
                <option value="one23reg">123-reg</option>
                <option value="zoho">Zoho</option>
                <option value="google_workspace">Google Workspace (OAuth)</option>
                <option value="zen">Zen Internet</option>
                <option value="talktalk">TalkTalk Business</option>
                <option value="btconnect">BT Connect</option>
                <option value="other">Other (manual entry)</option>
              </select>
              <button
                type="button"
                onClick={() => runMxDetection(emailAddress)}
                disabled={mxDetecting || !emailAddress}
                data-testid="btn-retry-mx-detection"
                style={{ background: 'transparent',
                          color: '#0073EA',
                          border: '1px solid #cbd5e1',
                          padding: '0.45rem 0.85rem',
                          borderRadius: 6,
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap' }}
              >
                {mxDetecting ? 'Detecting…' : 'Retry detection'}
              </button>
              </div>
              <small>
                Auto-detection uses your domain's MX records. Override
                this only if the detection is wrong, or click
                "Retry detection" to run the lookup again.
              </small>
            </div>

            {/* Microsoft 365 OAuth banner (post-callback success/error) */}
            {ms365Banner && (
              <div
                className={`email-conn-badge ${ms365Banner.kind === 'success' ? 'email-conn-green' : 'email-conn-red'}`}
                data-testid="ms365-banner"
                style={{ marginBottom: '0.75rem' }}
              >
                <span className={`ecb-dot ${ms365Banner.kind === 'error' ? 'ecb-dot-red' : ''}`}></span>
                {ms365Banner.text}
              </div>
            )}

            {/* Microsoft 365 OAuth-only block (triggered by godaddy_m365 OR
                microsoft365_oauth OR MX-detected M365) */}
            {(effectiveProvider === 'godaddy_m365'
                || effectiveProvider === 'microsoft365_oauth') && emailAddress && !emailDomainError && (
              <div className="form-group" data-testid="ms365-oauth-block">
                <p className="settings-help" style={{ marginTop: 0 }}>
                  Microsoft no longer supports password-based IMAP/SMTP for
                  Microsoft 365 mailboxes. Connect securely with Microsoft
                  using a one-time sign-in — Estatel AI never sees your password.
                </p>
                {ms365Status.connected ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div className="email-conn-badge email-conn-green" data-testid="ms365-connected-badge">
                      <span className="ecb-dot"></span>
                      Microsoft 365 connected — {ms365Status.upn}
                    </div>
                    <button
                      className="btn-danger"
                      onClick={handleMs365Disconnect}
                      data-testid="btn-ms365-disconnect"
                    >
                      Disconnect Microsoft 365
                    </button>
                    {/* FIX (2026-05-23): Send Test Email also needed for the
                        MS365 OAuth path so the agent can verify the Graph
                        send leg without waiting for a real inbound. */}
                    <button
                      className="btn-primary"
                      onClick={handleSendTestEmail}
                      disabled={sendingTest}
                      data-testid="btn-send-test-ms365"
                    >
                      {sendingTest ? 'Sending...' : 'Send Test Email to Myself'}
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleMs365Connect}
                    disabled={ms365Connecting || !ms365Status.configured}
                    data-testid="btn-ms365-connect"
                  >
                    {ms365Connecting ? 'Redirecting…' : 'Connect Microsoft 365'}
                  </button>
                )}
                {!ms365Status.configured && (
                  <small className="form-error" data-testid="ms365-not-configured">
                    Microsoft 365 OAuth is not configured on the server. Contact your administrator.
                  </small>
                )}
              </div>
            )}

            {/* Google Workspace OAuth — environment variables required to activate */}
            {effectiveProvider === 'google_workspace' && emailAddress && !emailDomainError && (
              <div className="form-group" data-testid="google-oauth-block">
                {mxDetected?.google_oauth_available ? (
                  <button className="btn-primary" disabled data-testid="btn-google-connect">
                    Connect Google (coming soon)
                  </button>
                ) : (
                  <div className="email-error-box" data-testid="google-coming-soon">
                    Google Workspace connection coming soon.
                  </div>
                )}
              </div>
            )}

            {/* Password-based providers — show password field.
                FIX 8: hide once connected; agent must Disconnect first. */}
            {effectiveProvider
              && effectiveProvider !== 'godaddy_m365'
              && effectiveProvider !== 'microsoft365_oauth'
              && effectiveProvider !== 'google_workspace'
              && emailAddress && !emailDomainError
              && connectionStatus !== 'connected' && (
              <div className="form-grid" data-testid="email-provider-fields">
                <div className="form-group">
                  <label>Email account password *</label>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    placeholder={hasPassword ? '••••••••' : 'The password you use to log into your email account'}
                    data-testid="input-email-password"
                  />
                  {hasPassword && !emailPassword && (
                    <small>Password is saved. Leave blank to keep current password.</small>
                  )}
                  {(effectiveProvider === 'godaddy_m365' || effectiveProvider === 'microsoft365_oauth') && (
                    <small className="form-hint" data-testid="m365-app-password-hint">
                      If your Microsoft 365 account has two-step verification turned on,
                      enter an <strong>app password</strong> here (generated from{' '}
                      <a href="https://aka.ms/MyApps" target="_blank" rel="noopener noreferrer">
                        aka.ms/MyApps → Security info → App passwords
                      </a>), not your normal login password.
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Manual server settings — for "Other" OR when MX didn't detect
                a provider and the user is on Auto-detected.
                FIX 8: hide once connected. */}
            {(effectiveProvider === 'other'
              || (emailProvider === 'auto' && mxDetected && !mxDetected.detected)
              ) && emailAddress && !emailDomainError
              && connectionStatus !== 'connected' && (
              <div className="form-grid" style={{ marginTop: '0.5rem' }}
                   data-testid="email-manual-server-fields">
                <div className="form-group">
                  <label>IMAP Server *</label>
                  <input type="text" value={imapHost}
                         onChange={(e) => setImapHost(e.target.value)}
                         placeholder="imap.yourprovider.com"
                         data-testid="input-imap-host" />
                </div>
                <div className="form-group">
                  <label>IMAP Port *</label>
                  <input type="number" value={imapPort}
                         onChange={(e) => setImapPort(e.target.value)}
                         placeholder="993" data-testid="input-imap-port" />
                </div>
                <div className="form-group">
                  <label>SMTP Server *</label>
                  <input type="text" value={smtpHost}
                         onChange={(e) => setSmtpHost(e.target.value)}
                         placeholder="smtp.yourprovider.com"
                         data-testid="input-smtp-host" />
                </div>
                <div className="form-group">
                  <label>SMTP Port *</label>
                  <input type="number" value={smtpPort}
                         onChange={(e) => setSmtpPort(e.target.value)}
                         placeholder="587" data-testid="input-smtp-port" />
                </div>
                {/* Password field for manual/unknown providers — shown here
                    because the password-fields block above only renders when
                    effectiveProvider is a known password preset. */}
                {(effectiveProvider === 'other'
                  || (emailProvider === 'auto' && mxDetected && !mxDetected.detected)) && (
                  <div className="form-group">
                    <label>Email account password *</label>
                    <input
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder={hasPassword ? '••••••••' : 'The password you use to log into your email account'}
                      data-testid="input-email-password-manual"
                    />
                    {hasPassword && !emailPassword && (
                      <small>Password is saved. Leave blank to keep current password.</small>
                    )}
                  </div>
                )}
              </div>
            )}

            {effectiveProvider
              && effectiveProvider !== 'godaddy_m365'
              && effectiveProvider !== 'microsoft365_oauth'
              && effectiveProvider !== 'google_workspace' && (
              <div style={{display:'flex',gap:'0.75rem',alignItems:'center',marginTop:'0.75rem'}}>
                {/* FIX 8 (2026-05-23): once an email is connected, hide the
                    Connect/password retest path and offer Disconnect only.
                    Agent must explicitly disconnect to switch addresses. */}
                {connectionStatus === 'connected' ? (
                  <button
                    className="btn-danger"
                    onClick={handleDisconnectEmail}
                    data-testid="btn-disconnect-email"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleTestConnection}
                    disabled={
                      testingConnection
                      || !emailAddress
                      || !!emailDomainError
                      || (!emailPassword && !hasPassword)
                      || !imapHost || !smtpHost
                    }
                    data-testid="btn-test-connection"
                  >
                    {testingConnection ? 'Testing...' : 'Connect'}
                  </button>
                )}
                <button className="btn-primary" onClick={handleSendTestEmail}
                        disabled={sendingTest || connectionStatus !== 'connected'}
                        data-testid="btn-send-test">
                  {sendingTest ? 'Sending...' : 'Send Test Email to Myself'}
                </button>
              </div>
            )}

            {/* Step-by-step test progress */}
            {testSteps.length > 0 && (
              <div className="test-steps" data-testid="test-steps">
                {testSteps.map((s, i) => (
                  <div key={i} className={`test-step test-step-${s.status}`} data-testid={`test-step-${s.step}`}>
                    <span className="ts-icon">{s.status === 'ok' ? '\u2713' : s.status === 'fail' ? '\u2717' : '\u25CF'}</span>
                    <span className="ts-label">{s.label}</span>
                    {s.error && <div className="ts-error">{s.error}</div>}
                  </div>
                ))}
              </div>
            )}

            {connectionError && !testSteps.length && (
              <div className="email-error-box" data-testid="email-error">{connectionError}</div>
            )}
          </div>
          
          {/* Email Preferences */}
          <div className="settings-section">
            <h2>Email Preferences</h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Sender Display Name *</label>
                <input 
                  type="text" 
                  value={senderName} 
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="James at Acme Lettings"
                  data-testid="input-sender-name"
                />
              </div>
              
              <div className="form-group">
                <label>Email Tone</label>
                <select 
                  value={emailTone} 
                  onChange={(e) => setEmailTone(e.target.value)}
                  data-testid="select-email-tone"
                >
                  <option value="formal">Formal</option>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Sign-off Phrase</label>
                <input 
                  type="text" 
                  value={signoffPhrase} 
                  onChange={(e) => setSignoffPhrase(e.target.value)}
                  placeholder="Kind regards"
                  data-testid="input-signoff-phrase"
                />
              </div>
            </div>
            
            <div className="signature-editor">
              <div className="signature-input">
                <label>HTML Signature</label>
                <textarea 
                  value={signatureHtml} 
                  onChange={(e) => setSignatureHtml(e.target.value)}
                  placeholder="<p>Your Company Name<br/>Phone: +44 123 456 7890<br/>Email: info@yourcompany.com</p>"
                  rows="8"
                  data-testid="textarea-signature"
                />

                {/* 2026-02 — Image signature upload (PNG / JPG / SVG, max 2MB) */}
                <div className="signature-image-uploader" style={{ marginTop: '1rem' }}>
                  <label>Signature Image (optional)</label>
                  <p className="section-description" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                    Upload a PNG, JPG, or SVG of your handwritten or branded signature. Appears at the bottom of every outbound email below the text signature at 200px wide.
                  </p>
                  {!signatureImageUrl ? (
                    <>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={handleSignatureImageUpload}
                        disabled={signatureImageUploading}
                        data-testid="input-signature-image-upload"
                      />
                      {signatureImageUploading && (
                        <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>Uploading…</span>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }} data-testid="signature-image-current">
                      <img
                        src={signatureImageUrl}
                        alt="Signature"
                        style={{ maxWidth: '200px', height: 'auto', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff' }}
                      />
                      <button
                        type="button"
                        onClick={handleSignatureImageRemove}
                        style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '0.45rem 0.9rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
                        data-testid="btn-remove-signature-image"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                  {signatureImageError && (
                    <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.5rem' }} data-testid="signature-image-error">
                      {signatureImageError}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="signature-preview">
                <label>Live Preview</label>
                <div className="preview-box">
                  <div className="preview-email">
                    <p>Dear Tenant,</p>
                    <p>This is how your email signature will appear.</p>
                    <p>{signoffPhrase || 'Best regards'},<br/>{senderName || 'Your Name'}</p>
                    {/* FIX 10 (2026-05-23): the textarea stores raw text — convert
                        newline characters to <br> so pressing Enter in the editor
                        actually wraps a line in the preview, matching how the
                        recipient will see it. */}
                    <div dangerouslySetInnerHTML={{__html: (signatureHtml || '').replace(/\n/g,'<br>')}} />
                    {signatureImageUrl && (
                      <div style={{ marginTop: '8px' }} data-testid="signature-image-preview">
                        <img
                          src={signatureImageUrl}
                          alt="Signature"
                          style={{ maxWidth: '200px', width: '200px', height: 'auto', display: 'block', border: 0 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Autonomy Mode */}
          {/* 2026-02 (Item 2) — `supervised` mode removed. It was
              functionally identical to full-autonomy (send-
              immediately-unless-escalation) so the label was
              misleading. Only two modes: draft-and-approve (queue)
              and full-autonomy (auto-send routine). Legacy accounts
              stored as 'supervised' are normalised to
              draft-and-approve on read (see autonomy.py). */}
          <div className="settings-section">
            <h2>Email Autonomy Mode</h2>
            <p className="section-description">
              Current mode: <strong>{autonomyMode === 'full_autonomy' ? 'Full Autonomy' : 'Draft and Approve'}</strong>
            </p>

            {autonomyMode === 'full_autonomy' && (
              <div className="mode-description mode-warning">
                <p>⚠️ <strong>Full Autonomy</strong>: AI sends routine emails without your review. Legal and sensitive emails are still held for approval. Use with caution.</p>
              </div>
            )}

            {autonomyMode !== 'full_autonomy' && (
              <div className="mode-description">
                <p>📝 <strong>Draft and Approve</strong>: AI drafts all emails but never sends without your explicit approval. Maximum control.</p>
              </div>
            )}

            <div className="form-group">
              <label>Change Mode</label>
              <select
                value={autonomyMode === 'full_autonomy' ? 'full_autonomy' : 'draft_and_approve'}
                onChange={(e) => handleAutonomyModeChange(e.target.value)}
                data-testid="select-autonomy-mode"
              >
                <option value="draft_and_approve">Draft and Approve (Recommended)</option>
                <option value="full_autonomy">Full Autonomy</option>
              </select>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="settings-actions">
            <button 
              className="btn-primary btn-large"
              onClick={handleSaveSettings}
              disabled={saving}
              data-testid="btn-save-settings"
            >
              {saving ? 'Saving...' : '💾 Save Settings'}
            </button>
            {/* FIX (2026-05-23): user reported Save button is at the bottom
                of the panel and the top-of-panel banner is off-screen on
                save — duplicate the banner right under the button so the
                confirmation is visible at the same scroll position. */}
            {saveBanner && (
              <div
                className={`status-banner ${saveBanner.kind === 'success' ? 'status-connected' : 'status-error'}`}
                style={{ marginTop: '0.75rem' }}
                data-testid={`save-banner-bottom-${saveBanner.kind}`}
              >
                {saveBanner.kind === 'success' ? '✅ ' : '❌ '}{saveBanner.text}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* WhatsApp Integration Tab — gated by REACT_APP_WHATSAPP_ENABLED.
          When the flag is false (default) this entire section is hidden but
          the underlying state, handlers, backend routes and integrations
          remain intact for one-line reactivation. */}
      {WHATSAPP_ENABLED && activeTab === 'whatsapp' && (
        <div className="settings-content">
          
          {/* Connection Status Banner */}
          <div className={`status-banner ${whatsappConnectionMode === '360dialog' ? 'status-connected' : 'status-not-connected'}`} data-testid="whatsapp-status">
            {whatsappConnectionMode === '360dialog' ? '✅ 360dialog connected' : '⚠️ Running on Baileys session — upgrade to 360dialog when your first client signs.'}
          </div>
          
          {/* 360dialog Configuration */}
          <div className="settings-section">
            <h2>360dialog Configuration</h2>
            <p className="section-description">
              Connect your 360dialog account for WhatsApp Business API access. 
              Leave empty to use Baileys mode (free tier with limited features).
            </p>
            
            <div className="form-grid">
              <div className="form-group">
                <label>WhatsApp Business Number</label>
                <input 
                  type="text" 
                  value={whatsappBusinessNumber} 
                  onChange={(e) => setWhatsappBusinessNumber(e.target.value)}
                  placeholder="+447911123456"
                  data-testid="input-whatsapp-number"
                />
                <small>E.164 format (e.g., +447911123456)</small>
              </div>
              
              <div className="form-group">
                <label>360dialog API Key</label>
                <input 
                  type="password" 
                  value={whatsappApiKey} 
                  onChange={(e) => setWhatsappApiKey(e.target.value)}
                  placeholder={hasWhatsappApiKey ? '••••••••' : 'Enter 360dialog API key'}
                  data-testid="input-whatsapp-api-key"
                />
                {hasWhatsappApiKey && !whatsappApiKey && (
                  <small>API key is saved. Leave blank to keep current key.</small>
                )}
              </div>
            </div>
            
            <button 
              className="btn-primary"
              onClick={handleSaveWhatsAppSettings}
              disabled={saving}
              data-testid="btn-save-whatsapp-settings"
            >
              {saving ? 'Saving...' : '💾 Save WhatsApp Settings'}
            </button>
          </div>
          
          {/* WhatsApp Templates */}
          <div className="settings-section">
            <h2>WhatsApp Message Templates</h2>
            <p className="section-description">
              Pre-approved message templates for WhatsApp Business. 
              These templates are required for messages outside the 24-hour session window.
            </p>
            
            {whatsappTemplates.length === 0 ? (
              <div className="empty-state">
                <p>No templates loaded yet.</p>
                <button 
                  className="btn-secondary"
                  onClick={handleSyncTemplates}
                  disabled={syncingTemplates}
                  data-testid="btn-sync-templates"
                >
                  {syncingTemplates ? 'Loading...' : '🔄 Load Required Templates'}
                </button>
              </div>
            ) : (
              <>
                <div className="template-list">
                  {whatsappTemplates.map((template, index) => (
                    <div key={index} className="template-card" data-testid={`template-${template.template_name}`}>
                      <div className="template-header">
                        <h3>{template.template_name.replace('_', ' ').toUpperCase()}</h3>
                        <span className={`badge badge-${template.approval_status}`}>
                          {template.approval_status}
                        </span>
                      </div>
                      <div className="template-body">
                        <p>{template.template_body}</p>
                      </div>
                      <div className="template-variables">
                        <strong>Variables:</strong> {template.variables.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="info-message" style={{marginTop: '1rem'}}>
                  ℹ️ <strong>Note:</strong> Additional templates can be added in a future update.
                </div>
              </>
            )}
          </div>
          
          {/* 24-Hour Window Info */}
          <div className="settings-section">
            <h2>📱 WhatsApp 24-Hour Session Window</h2>
            <p className="section-description">
              WhatsApp Business API has a 24-hour session window policy:
            </p>
            <ul style={{marginLeft: '2rem', lineHeight: '1.8'}}>
              <li><strong>Within 24 hours:</strong> You can send free-form messages after a tenant messages you.</li>
              <li><strong>After 24 hours:</strong> You must use a pre-approved template to re-engage.</li>
              <li><strong>Unknown contacts:</strong> Messages from unknown numbers create alerts on your dashboard (no auto-response).</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* 2026-02 — AI Assistant Tab. Controls the persona name used by
          the lead-triage AI when replying to inbound emails / messages. */}
      {activeTab === 'ai_assistant' && (
        <div className="settings-content" data-testid="ai-assistant-tab-content">
          <div className="settings-section">
            <h2>AI Assistant</h2>
            <p className="section-description">
              Configure how your AI assistant introduces itself to new contacts who reach out by email. The assistant handles your inbound enquiries, classifies the contact, and gathers the details your team needs before handing the conversation over.
            </p>

            <div className="form-group" style={{ maxWidth: '480px' }}>
              <label htmlFor="ai-persona-name-input">AI Assistant Name</label>
              <input
                id="ai-persona-name-input"
                type="text"
                value={aiPersonaName}
                onChange={(e) => setAiPersonaName(e.target.value)}
                placeholder="e.g. Olivia"
                maxLength={40}
                data-testid="input-ai-persona-name"
              />
              <small style={{ display: 'block', color: '#64748b', marginTop: '0.4rem' }}>
                This is the name your AI assistant uses when responding to enquiries. Leave blank to respond as your agency directly without a persona name.
              </small>
            </div>

            <div className="form-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.75rem' }}>
              <button
                onClick={handleSaveAiPersona}
                disabled={aiPersonaSaving}
                style={{
                  background: aiPersonaSaving ? '#94a3b8' : '#0073EA',
                  color: '#fff', border: 'none',
                  padding: '0.55rem 1.1rem', borderRadius: 4,
                  cursor: aiPersonaSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
                data-testid="btn-save-ai-persona"
              >
                {aiPersonaSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={handleClearAiPersona}
                disabled={aiPersonaSaving}
                style={{
                  background: '#e2e8f0', color: '#1e293b', border: 'none',
                  padding: '0.55rem 1.1rem', borderRadius: 4,
                  cursor: aiPersonaSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
                data-testid="btn-clear-ai-persona"
              >
                Clear
              </button>
              {aiPersonaBanner && (
                <span
                  data-testid={`ai-persona-banner-${aiPersonaBanner.kind}`}
                  style={{
                    fontSize: '0.95rem',
                    color: aiPersonaBanner.kind === 'success' ? '#16a34a' : '#dc2626',
                    fontWeight: 500,
                  }}
                >
                  {aiPersonaBanner.kind === 'success' ? '✅ ' : '❌ '}{aiPersonaBanner.text}
                </span>
              )}
            </div>

            {/* Live preview */}
            <div style={{ marginTop: '1.5rem', maxWidth: '640px' }}>
              <label style={{ fontWeight: 600, color: '#0f172a' }}>Live preview — opening line</label>
              <p className="section-description" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                This is how your assistant will introduce itself to a new contact.
              </p>
              <div
                className="preview-box"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  padding: '1rem 1.1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  color: '#0f172a',
                  lineHeight: 1.55,
                }}
                data-testid="ai-persona-preview"
              >
                {(() => {
                  const persona = (aiPersonaName || '').trim();
                  const agency = senderName || 'your agency';
                  if (persona) {
                    return `Hi [contact name], I'm ${persona} and I help the team at ${agency} with new enquiries.`;
                  }
                  return `Hi [contact name], thanks for getting in touch with ${agency}.`;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Integrations Tab - OpenClaw Configuration */}
      {activeTab === 'integrations' && (
        <div className="settings-content">
          
          {/* Connection Status Banner */}
          <div className={`status-banner ${
            openclawConnectionStatus === 'active' ? 'status-connected' : 
            openclawConnectionStatus === 'inactive' ? 'status-not-connected' : 
            'status-neutral'
          }`} data-testid="openclaw-status">
            {openclawConnectionStatus === 'active' && '✅ OpenClaw connected (Active)'}
            {openclawConnectionStatus === 'inactive' && '⚠️ OpenClaw inactive (Last used > 24 hours ago)'}
            {openclawConnectionStatus === 'never_connected' && '⚪ Never connected'}
          </div>
          
          {/* API Key Management */}
          <div className="settings-section">
            <h2>🔑 OpenClaw API Key</h2>
            <p className="section-description">
              This API key authenticates OpenClaw when querying Estatel AI. Keep it secure.
            </p>
            
            <div className="api-key-display">
              <div className="api-key-field">
                <label>API Key</label>
                <div className="api-key-value">
                  <code>{openclawApiKeyMasked}</code>
                </div>
              </div>
              
              <div className="api-key-actions">
                <button 
                  className="btn-secondary"
                  onClick={async () => {
                    try {
                      if (openclawApiKeyFull) {
                        await navigator.clipboard.writeText(openclawApiKeyFull);
                        alert('✅ API key copied to clipboard');
                      } else {
                        alert('⚠️ Full API key not available. Please regenerate to get the full key.');
                      }
                    } catch (error) {
                      alert('Error copying API key: ' + error.message);
                    }
                  }}
                  data-testid="btn-copy-api-key"
                  disabled={!openclawApiKeyFull}
                  title={!openclawApiKeyFull ? 'Regenerate key to copy full value' : 'Copy to clipboard'}
                >
                  📋 Copy Key
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => setShowRegenerateDialog(true)}
                  data-testid="btn-regenerate-key"
                >
                  🔄 Regenerate Key
                </button>
              </div>
            </div>
            
            <div className="api-key-meta">
              <div className="meta-item">
                <strong>Last Used:</strong> {openclawLastUsed ? formatDate(openclawLastUsed) : 'Never'}
              </div>
              <div className="meta-item">
                <strong>Status:</strong> 
                <span className={`connection-badge badge-${openclawConnectionStatus}`}>
                  {openclawConnectionStatus === 'active' ? 'Active' : 
                   openclawConnectionStatus === 'inactive' ? 'Inactive' : 
                   'Never Connected'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Configuration Instructions */}
          <div className="settings-section">
            <h2>⚙️ Configuration Instructions</h2>
            <p className="section-description">
              Add these environment variables to your OpenClaw configuration on ClawHosters.
            </p>
            
            <div className="config-blocks">
              <div className="config-block">
                <div className="config-header">
                  <strong>ESTATEIQ_API_URL</strong>
                  <button 
                    className="btn-copy-small"
                    onClick={() => copyToClipboard(openclawApiUrl)}
                    data-testid="btn-copy-api-url"
                  >
                    Copy
                  </button>
                </div>
                <code className="config-value">{openclawApiUrl}</code>
              </div>
              
              <div className="config-block">
                <div className="config-header">
                  <strong>ESTATEIQ_API_KEY</strong>
                  <button 
                    className="btn-copy-small"
                    onClick={() => openclawApiKeyFull ? copyToClipboard(openclawApiKeyFull) : alert('Regenerate key first')}
                    data-testid="btn-copy-env-key"
                  >
                    Copy
                  </button>
                </div>
                <code className="config-value">{openclawApiKeyMasked}</code>
              </div>
              
              <div className="config-block">
                <div className="config-header">
                  <strong>AGENT_CLIENT_ID</strong>
                  <button 
                    className="btn-copy-small"
                    onClick={() => copyToClipboard(openclawClientId)}
                    data-testid="btn-copy-client-id"
                  >
                    Copy
                  </button>
                </div>
                <code className="config-value">{openclawClientId}</code>
              </div>
            </div>
          </div>
          
          {/* OpenClaw Pre-Response Protocol */}
          <div className="settings-section">
            <h2>🤖 OpenClaw Pre-Response Protocol</h2>
            <p className="section-description">
              Before sending ANY response to ANY tenant on ANY channel, OpenClaw must follow these steps:
            </p>
            
            <div className="protocol-steps">
              <div className="protocol-step">
                <span className="step-number">1</span>
                <span className="step-text">Receive inbound message with sender identifier (phone or email)</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">2</span>
                <span className="step-text">Call <code>GET /api/tenant/lookup</code> with identifier</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">3</span>
                <span className="step-text">If 404 returned → do not respond. Create alert. Stop.</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">4</span>
                <span className="step-text">If found → receive full context object</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">5</span>
                <span className="step-text">Check escalation rules → does this message require human review?</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">6</span>
                <span className="step-text">If escalation required → call <code>POST /api/pending-message</code>. Do NOT send response.</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">7</span>
                <span className="step-text">If no escalation → draft response using full context and agent tone/settings</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">8</span>
                <span className="step-text">Send via correct channel</span>
              </div>
              <div className="protocol-step">
                <span className="step-number">9</span>
                <span className="step-text">Call <code>POST /api/interaction</code> to log the full exchange</span>
              </div>
            </div>
          </div>
          
          {/* Escalation Rules */}
          <div className="settings-section">
            <h2>⚠️ Escalation Rules</h2>
            <p className="section-description">
              OpenClaw MUST escalate to pending-message queue if ANY of these are true:
            </p>
            
            <div className="escalation-rules">
              <div className="escalation-rule">
                <span className="rule-label">E1</span>
                <span className="rule-text">Message contains keywords: <code>notice, court, solicitor, possession, eviction, claim, legal, dispute, deposit dispute, section 8</code> (case-insensitive)</span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E2</span>
                <span className="rule-text">Tenant has an active Case record with <code>current_stage &gt;= 1</code></span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E3</span>
                <span className="rule-text">Tenant arrears balance <code>&gt;= 2 months rent</code></span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E4</span>
                <span className="rule-text">Any previous Interaction for this tenant has <code>flagged_for_review = true</code></span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E5</span>
                <span className="rule-text">This is the first ever interaction with this tenant (<code>recent_interactions</code> array is empty)</span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E6</span>
                <span className="rule-text">Tenant message explicitly asks to speak to a human, manager, or solicitor</span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E7</span>
                <span className="rule-text">Agent <code>autonomy_mode = draft</code> — all messages escalate in this mode</span>
              </div>
              <div className="escalation-rule">
                <span className="rule-label">E8</span>
                <span className="rule-text"><code>Case.current_stage &gt;= 3</code> — active Section 8, all communications held for review</span>
              </div>
            </div>
            
            <div className="info-message" style={{marginTop: '1.5rem'}}>
              ℹ️ <strong>When escalating:</strong> Include <code>flag_reason</code> in the pending message explaining which rule was triggered.
            </div>
          </div>
        </div>
      )}
      
      {/* Notifications Settings Tab */}
      {activeTab === 'notifications' && (
        <div className="settings-content" data-testid="notifications-tab-content">
          
          {/* Daily Digest Section */}
          <div className="settings-section">
            <h2>Daily Digest</h2>
            <p className="section-description">
              Receive a daily summary of pending approvals, active cases, arrears, compliance deadlines, and more.
            </p>
            
            <div className="form-group">
              <label>Enable Daily Digest</label>
              <div className="toggle-row">
                <button
                  className={`toggle-btn ${notifDigestEnabled ? 'toggle-on' : 'toggle-off'}`}
                  onClick={() => setNotifDigestEnabled(!notifDigestEnabled)}
                  data-testid="toggle-digest-enabled"
                >
                  {notifDigestEnabled ? 'ON' : 'OFF'}
                </button>
                <span className="toggle-label">{notifDigestEnabled ? 'Digest will be sent daily' : 'Digest is paused'}</span>
              </div>
            </div>
            
            {notifDigestEnabled && (
              <>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Delivery Time (UTC)</label>
                    <input
                      type="time"
                      value={notifDigestTime}
                      onChange={e => setNotifDigestTime(e.target.value)}
                      data-testid="input-digest-time"
                    />
                  </div>
                  <div className="form-group">
                    <label>Digest Channel</label>
                    <select
                      value={notifDigestChannel}
                      onChange={e => setNotifDigestChannel(e.target.value)}
                      data-testid="select-digest-channel"
                    >
                      <option value="email">Email</option>
                      {WHATSAPP_ENABLED && <option value="whatsapp">WhatsApp</option>}
                      {WHATSAPP_ENABLED && <option value="both">Both</option>}
                    </select>
                  </div>
                </div>
                
                <div className="form-actions" style={{marginTop: '0.75rem'}}>
                  <button
                    className="btn-primary"
                    onClick={handleSendTestDigest}
                    disabled={notifSendingDigest}
                    data-testid="btn-send-test-digest"
                  >
                    {notifSendingDigest ? 'Sending...' : 'Send Test Digest Now'}
                  </button>
                </div>
              </>
            )}
          </div>
          
          {/* Alert Channel Settings */}
          <div className="settings-section">
            <h2>Alert Channels</h2>
            <p className="section-description">
              Choose how you receive real-time alerts for pending messages, compliance expirations, and case deadlines.
            </p>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Standard Alert Channel</label>
                <select
                  value={notifAlertChannel}
                  onChange={e => setNotifAlertChannel(e.target.value)}
                  data-testid="select-alert-channel"
                >
                  <option value="email">Email</option>
                  {WHATSAPP_ENABLED && <option value="whatsapp">WhatsApp</option>}
                  {WHATSAPP_ENABLED && <option value="both">Both</option>}
                </select>
              </div>
              <div className="form-group">
                <label>Urgent / Critical Alert Channel</label>
                <select
                  value={notifUrgentChannel}
                  onChange={e => setNotifUrgentChannel(e.target.value)}
                  data-testid="select-urgent-channel"
                >
                  <option value="email">Email</option>
                  {WHATSAPP_ENABLED && <option value="whatsapp">WhatsApp</option>}
                  {WHATSAPP_ENABLED && <option value="both">Both</option>}
                </select>
              </div>
            </div>
          </div>
          
          {/* Contact Details */}
          <div className="settings-section">
            <h2>Notification Contacts</h2>
            <p className="section-description">
              Where alerts and digests should be delivered. Defaults to your account email if left blank.
            </p>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Notification Email</label>
                <input
                  type="email"
                  value={notifEmail}
                  onChange={e => setNotifEmail(e.target.value)}
                  placeholder="your@email.co.uk"
                  data-testid="input-notif-email"
                />
              </div>
              {WHATSAPP_ENABLED && (
                <div className="form-group">
                  <label>Notification WhatsApp Number</label>
                  <input
                    type="text"
                    value={notifWhatsappNumber}
                    onChange={e => setNotifWhatsappNumber(e.target.value)}
                    placeholder="+447700900123"
                    data-testid="input-notif-whatsapp"
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Step 40 — Staff WhatsApp Access (multi-number with roles) */}
          {WHATSAPP_ENABLED && (
          <div className="settings-section" data-testid="staff-whatsapp-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h2>Staff WhatsApp Access</h2>
              <button
                onClick={() => { setStaffForm({ ...EMPTY_STAFF }); setStaffError(null); }}
                style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
                data-testid="btn-add-staff-member"
              >
                + Add Staff Member
              </button>
            </div>
            <p className="section-description">
              Authorise additional staff WhatsApp numbers to interact with Estatel AI. Each staff member is assigned a role that controls what they can do.
            </p>

            <table className="landlord-table" data-testid="staff-numbers-table" style={{ width: '100%', marginTop: '0.75rem' }}>
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Phone Number</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Primary admin row — read-only */}
                <tr data-testid="staff-row-primary" style={{ color: '#1e293b' }}>
                  <td style={{ color: '#1e293b', fontWeight: 500 }}><strong>Primary Admin</strong></td>
                  <td style={{ color: '#1e293b' }}>{notifWhatsappNumber || <em style={{ color: '#94a3b8' }}>Not set</em>}</td>
                  <td>
                    <span style={{ background: '#10b981', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>
                      ADMIN
                    </span>
                  </td>
                  <td>
                    <span style={{ color: '#16a34a', fontWeight: 500 }}>Active</span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#94a3b8', fontSize: '0.85rem' }}>
                    Set above
                  </td>
                </tr>
                {/* Staff rows */}
                {staffNumbers.map(s => (
                  <tr key={s.id} data-testid={`staff-row-${s.id}`} style={{ color: '#1e293b' }}>
                    <td style={{ color: '#1e293b', fontWeight: 500 }}>{s.display_name}</td>
                    <td style={{ color: '#1e293b' }}>{s.phone_number}</td>
                    <td>
                      {s.role === 'admin' && (
                        <span style={{ background: '#10b981', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>ADMIN</span>
                      )}
                      {s.role === 'standard' && (
                        <span style={{ background: '#0073EA', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>STANDARD</span>
                      )}
                      {s.role === 'readonly' && (
                        <span style={{ background: '#94a3b8', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600 }}>READ ONLY</span>
                      )}
                    </td>
                    <td>
                      {s.is_active
                        ? <span style={{ color: '#16a34a', fontWeight: 500 }}>Active</span>
                        : <span style={{ color: '#dc2626', fontWeight: 500 }}>Inactive</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => { setStaffForm({ id: s.id, display_name: s.display_name, phone_number: s.phone_number, role: s.role }); setStaffError(null); }}
                        style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.35rem 0.7rem', borderRadius: 4, cursor: 'pointer', marginRight: 6, fontSize: '0.85rem' }}
                        data-testid={`btn-staff-edit-${s.id}`}
                      >
                        Edit
                      </button>
                      {s.is_active ? (
                        <button
                          onClick={() => handleDeactivateStaff(s.id)}
                          style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '0.35rem 0.7rem', borderRadius: 4, cursor: 'pointer', marginRight: 6, fontSize: '0.85rem' }}
                          data-testid={`btn-staff-deactivate-${s.id}`}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivateStaff(s.id)}
                          style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '0.35rem 0.7rem', borderRadius: 4, cursor: 'pointer', marginRight: 6, fontSize: '0.85rem' }}
                          data-testid={`btn-staff-reactivate-${s.id}`}
                        >
                          Reactivate
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveStaff(s.id, s.display_name)}
                        style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '0.35rem 0.7rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem' }}
                        data-testid={`btn-staff-remove-${s.id}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {staffNumbers.length === 0 && !staffLoading && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }} data-testid="staff-empty">
                      No additional staff members added. Click "Add Staff Member" to authorise another WhatsApp number.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* Step 40 — Add/Edit Staff Number modal */}
          {WHATSAPP_ENABLED && staffForm && (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
              data-testid="staff-modal"
            >
              <div style={{ background: '#fff', borderRadius: 8, padding: '1.75rem', width: '460px', maxWidth: '92vw' }}>
                <h3 style={{ marginTop: 0 }}>{staffForm.id ? 'Edit Staff Member' : 'Add Staff Member'}</h3>

                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={staffForm.display_name}
                    onChange={e => setStaffForm({ ...staffForm, display_name: e.target.value })}
                    placeholder="e.g. Sarah Lettings"
                    data-testid="input-staff-display-name"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="text"
                    value={staffForm.phone_number}
                    onChange={e => setStaffForm({ ...staffForm, phone_number: e.target.value })}
                    placeholder="07911 123 456 or +447911123456"
                    disabled={!!staffForm.id}
                    style={staffForm.id ? { background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' } : {}}
                    data-testid="input-staff-phone"
                  />
                  {staffForm.id && (
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 }}>
                      Phone number cannot be changed. Remove this entry and add a new one to use a different number.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={staffForm.role}
                    onChange={e => setStaffForm({ ...staffForm, role: e.target.value })}
                    data-testid="select-staff-role"
                  >
                    <option value="admin">Admin — full access (uploads, status, all commands)</option>
                    <option value="standard">Standard — uploads + status + operational commands</option>
                    <option value="readonly">Read only — status and help only</option>
                  </select>
                </div>

                {staffError && (
                  <div
                    className="error-text"
                    role="alert"
                    style={{ background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.88rem', marginTop: '0.5rem' }}
                    data-testid="staff-error"
                  >
                    {staffError}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' }}>
                  <button
                    onClick={() => { setStaffForm(null); setStaffError(null); }}
                    disabled={staffSaving}
                    style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
                    data-testid="btn-staff-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveStaff}
                    disabled={staffSaving}
                    style={{ background: staffSaving ? '#94a3b8' : '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 4, cursor: staffSaving ? 'not-allowed' : 'pointer', fontWeight: 500 }}
                    data-testid="btn-staff-save"
                  >
                    {staffSaving ? 'Saving…' : (staffForm.id ? 'Update Staff Member' : 'Add Staff Member')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pending Reminder Timing */}
          <div className="settings-section">
            <h2>Pending Approval Reminders</h2>
            <p className="section-description">
              How long before you receive a reminder about unactioned pending messages.
            </p>
            
            <div className="form-group">
              <label>Reminder After</label>
              <select
                value={notifPendingReminderHours}
                onChange={e => setNotifPendingReminderHours(parseInt(e.target.value))}
                data-testid="select-pending-reminder"
              >
                <option value={4}>4 hours</option>
                <option value={8}>8 hours</option>
                <option value={24}>24 hours</option>
              </select>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="form-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' }}>
            <button
              style={{
                background: notifSaving ? '#94a3b8' : '#0073EA',
                color: '#fff', border: 'none',
                padding: '0.6rem 1.25rem', borderRadius: 4,
                cursor: notifSaving ? 'not-allowed' : 'pointer',
                fontWeight: 500,
              }}
              onClick={handleSaveNotificationSettings}
              disabled={notifSaving}
              data-testid="btn-save-notifications"
            >
              {notifSaving ? 'Saving...' : 'Save Notification Settings'}
            </button>
            {notifSaveMessage && (
              <span
                data-testid="notif-save-message"
                style={{
                  fontSize: '0.95rem',
                  color: notifSaveMessage.ok ? '#16a34a' : '#dc2626',
                  fontWeight: 500,
                }}
              >
                {notifSaveMessage.text}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Landlords Tab */}
      {activeTab === 'landlords' && (
        <div className="settings-content" data-testid="landlords-tab-content">
          <div className="settings-section">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem'}}>
              <h2>Landlord Profiles</h2>
              <button
                className="btn-primary"
                onClick={() => { setLandlordForm(null); setShowUnifiedFlow(true); }}
                data-testid="btn-add-landlord"
                style={{
                  background: '#0073EA', color: '#fff', border: 'none',
                  padding: '0.55rem 1.1rem', borderRadius: 6,
                  fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                + Add Landlord
              </button>
            </div>

            {/* CHANGE 2 — Unified Add-Landlord flow for NEW landlords ONLY.
                When `landlordForm` is non-null we are EDITING, so suppress
                the unified flow to avoid showing an empty add-new form
                alongside the populated edit form. */}
            {showUnifiedFlow && !landlordForm && (
              <UnifiedLandlordFlow
                apiBase={BACKEND_URL}
                onSaved={() => { fetchLandlords(); }}
                onClose={() => { setShowUnifiedFlow(false); fetchLandlords(); }}
              />
            )}

            {/* Landlord Form (Add/Edit) */}
            {landlordForm && (
              <div className="landlord-form" data-testid="landlord-form">
                <h3>{landlordForm._id ? 'Edit Landlord' : 'Add New Landlord'}</h3>
                <div className="form-grid">
                  {/* 2026-05-06 (Item 2) — Landlord type dropdown is the
                      first field on the form. Drives conditional rendering
                      of company-specific fields below. */}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Landlord type *</label>
                    <select
                      value={landlordForm.landlord_type || 'individual'}
                      onChange={e => setLandlordForm({
                        ...landlordForm,
                        landlord_type: e.target.value,
                      })}
                      data-testid="select-ll-type"
                      style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                      <option value="individual">Individual</option>
                      <option value="company">Limited company</option>
                    </select>
                  </div>

                  {/* When type === 'company' show Company name + Director
                      name. The single "Director name" input doubles as
                      the legacy `full_name` (kept in sync via shared
                      state) so the underlying record always carries a
                      valid full_name without showing two fields. */}
                  {(landlordForm.landlord_type || 'individual') === 'company' && (
                    <>
                      <div className="form-group">
                        <label>Company name *</label>
                        <input type="text"
                               value={landlordForm.company_name || ''}
                               onChange={e => setLandlordForm({...landlordForm, company_name: e.target.value})}
                               placeholder="Pemberton Properties Ltd"
                               data-testid="input-ll-company" />
                      </div>
                      <div className="form-group">
                        <label>Director name *</label>
                        <input type="text"
                               value={landlordForm.director_name || ''}
                               onChange={e => {
                                 const v = e.target.value;
                                 // Mirror director_name into full_name so
                                 // the underlying record always carries a
                                 // valid full_name (the legacy field is
                                 // still required by older code paths).
                                 setLandlordForm({
                                   ...landlordForm,
                                   director_name: v,
                                   full_name: v,
                                 });
                               }}
                               placeholder="Henry Pemberton"
                               data-testid="input-ll-director" />
                        <small style={{ color: '#475569', fontSize: '0.78rem', marginTop: '0.25rem', display: 'block' }}>
                          Enter the full name of the director who will sign
                          possession notices and court documents on behalf
                          of the company.
                        </small>
                      </div>
                    </>
                  )}

                  {/* The "Full Name" input is rendered ONLY for individual
                      landlords. For company landlords the Director name
                      field above doubles as the full_name (mirrored on
                      change), so we hide the separate Full Name row to
                      avoid showing two near-identical inputs. */}
                  {(landlordForm.landlord_type || 'individual') === 'individual' && (
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        value={landlordForm.full_name || ''}
                        onChange={e => setLandlordForm({
                          ...landlordForm, full_name: e.target.value,
                        })}
                        placeholder="John Smith"
                        data-testid="input-ll-name" />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Address Line 1 *</label>
                    <input type="text" value={landlordForm.address_line1} onChange={e => setLandlordForm({...landlordForm, address_line1: e.target.value})} data-testid="input-ll-addr1" />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input type="text" value={landlordForm.address_line2 || ''} onChange={e => setLandlordForm({...landlordForm, address_line2: e.target.value})} data-testid="input-ll-addr2" />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input type="text" value={landlordForm.city} onChange={e => setLandlordForm({...landlordForm, city: e.target.value})} data-testid="input-ll-city" />
                  </div>
                  <div className="form-group">
                    <label>Postcode *</label>
                    <input type="text" value={landlordForm.postcode} onChange={e => setLandlordForm({...landlordForm, postcode: e.target.value})} placeholder="SW1A 1AA" data-testid="input-ll-postcode" />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={landlordForm.email} onChange={e => setLandlordForm({...landlordForm, email: e.target.value})} placeholder="landlord@email.co.uk" data-testid="input-ll-email" />
                  </div>
                  <div className="form-group">
                    <label>Phone Number * (E.164)</label>
                    <input type="text" value={landlordForm.phone_number} onChange={e => setLandlordForm({...landlordForm, phone_number: e.target.value})} placeholder="+447911123456" data-testid="input-ll-phone" />
                  </div>
                </div>
                <div className="form-group" style={{marginTop:'0.75rem'}}>
                  <div className="toggle-row">
                    <button
                      className={`toggle-btn ${landlordForm.prs_database_registered ? 'toggle-on' : 'toggle-off'}`}
                      onClick={() => setLandlordForm({...landlordForm, prs_database_registered: !landlordForm.prs_database_registered})}
                      data-testid="toggle-prs"
                    >
                      {landlordForm.prs_database_registered ? 'YES' : 'NO'}
                    </button>
                    <span className="toggle-label">Registered on PRS Database</span>
                  </div>
                </div>

                {/* Step 53 BUG 5 — Let Property Address. Only on Add (not edit)
                    so we don't accidentally re-create a property on every save.
                    On edit, the agent manages let properties via the Linked
                    section below. */}
                {!landlordForm._id && (
                  <div data-testid="let-property-section" style={{ marginTop: '1.25rem', padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    <h4 style={{ marginTop: 0, marginBottom: '0.4rem' }}>Let Property Address</h4>
                    <p style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.85rem', color: '#475569' }}>
                      Enter the address of the property this landlord is letting (separate from their personal home address above).
                      Optional — leave blank if you'll add the property later.
                    </p>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Address Line 1</label>
                        <input type="text" value={landlordForm.let_address_line1 || ''} onChange={e => setLandlordForm({...landlordForm, let_address_line1: e.target.value})} data-testid="input-let-addr1" />
                      </div>
                      <div className="form-group">
                        <label>Address Line 2</label>
                        <input type="text" value={landlordForm.let_address_line2 || ''} onChange={e => setLandlordForm({...landlordForm, let_address_line2: e.target.value})} data-testid="input-let-addr2" />
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <input type="text" value={landlordForm.let_city || ''} onChange={e => setLandlordForm({...landlordForm, let_city: e.target.value})} data-testid="input-let-city" />
                      </div>
                      <div className="form-group">
                        <label>Postcode</label>
                        <input type="text" value={landlordForm.let_postcode || ''} onChange={e => setLandlordForm({...landlordForm, let_postcode: e.target.value})} placeholder="SW1A 1AA" data-testid="input-let-postcode" />
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-actions" style={{marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={handleSaveLandlord} disabled={landlordSaving} data-testid="btn-save-landlord">
                    {landlordSaving ? 'Saving...' : (landlordForm._id ? 'Update Landlord' : 'Create Landlord')}
                  </button>
                  <button className="btn-secondary" onClick={() => setLandlordForm(null)} data-testid="btn-cancel-landlord">Cancel</button>
                </div>

                {/* Step 52 BUG 4 — Let Properties section: visible only when
                    editing an existing landlord. Lists every property linked
                    to this landlord with its current tenancy status and a
                    link to the property detail page. */}
                {landlordForm._id && (
                  <LandlordLinkedProperties landlordId={landlordForm._id} apiBase={BACKEND_URL} />
                )}

                {/* 2026-05-29 — Add Property card on the Edit-Landlord
                    form so agents can register new Let/Available
                    properties for an existing landlord without going
                    through the full Add-Landlord flow. */}
                {landlordForm._id && (
                  <LandlordEditAddProperty
                    landlordId={landlordForm._id}
                    apiBase={BACKEND_URL}
                    onSaved={() => { fetchLandlords(); }}
                  />
                )}
              </div>
            )}

            {/* Landlord List Table */}
            {landlords.length === 0 && !landlordForm ? (
              <div
                data-testid="landlords-empty"
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '2.25rem 1.75rem',
                  textAlign: 'center',
                  color: '#475569',
                }}
              >
                <p style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600, color: '#0f172a' }}>
                  No landlords added yet.
                </p>
                <p style={{ margin: '0.5rem auto 0', maxWidth: 640, fontSize: '0.92rem', lineHeight: 1.55, color: '#475569' }}>
                  Every property must have a linked landlord before a Section 8 case can be started.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.25rem' }}>
                  <button
                    type="button"
                    onClick={() => { setLandlordForm(null); setShowUnifiedFlow(true); }}
                    data-testid="btn-add-landlord-empty"
                    style={{
                      background: '#0073EA',
                      color: '#fff',
                      border: 'none',
                      padding: '0.6rem 1.1rem',
                      borderRadius: 6,
                      fontSize: '0.92rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Add Landlord
                  </button>
                </div>
              </div>
            ) : (
              <table className="landlord-table" data-testid="landlords-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Company</th>
                    <th>Properties</th>
                    <th>PRS Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {landlords.map(ll => (
                    <tr key={ll._id} data-testid={`landlord-row-${ll._id}`}>
                      <td>{ll.full_name}</td>
                      <td>{ll.company_name || '—'}</td>
                      <td data-testid={`landlord-properties-${ll._id}`}>
                        {/* FIX 4B (onboarding wizard) — inline clickable
                            list of linked property addresses. Falls back
                            to the helpful "no properties linked" hint
                            when the count is zero. */}
                        {Array.isArray(ll.linked_properties) && ll.linked_properties.length > 0 ? (
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                            {ll.linked_properties.map(p => (
                              <li key={p.property_id}
                                  style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                                <a href={`/properties/${p.property_id}`}
                                   onClick={(e) => {
                                     e.preventDefault();
                                     window.location.hash = `properties/${p.property_id}`;
                                   }}
                                   data-testid={`landlord-property-link-${ll._id}-${p.property_id}`}
                                   style={{ color: '#0073EA', textDecoration: 'underline' }}>
                                  {p.address}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <em data-testid={`landlord-no-properties-${ll._id}`}
                              style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                            No properties linked — link a property from the
                            Properties page or from the dashboard banner.
                          </em>
                        )}
                      </td>
                      <td>
                        <span className={`prs-badge ${ll.prs_database_registered ? 'prs-yes' : 'prs-no'}`} data-testid={`prs-status-${ll._id}`}>
                          {ll.prs_database_registered ? 'Registered' : 'Not Registered'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <button className="btn-sm btn-edit"
                                onClick={() => { setShowUnifiedFlow(false); setLandlordForm({...ll}); }}
                                data-testid={`btn-edit-ll-${ll._id}`}>Edit</button>
                        <button className="btn-sm btn-delete" onClick={() => handleDeleteLandlord(ll._id, ll.full_name, ll.linked_properties_count)} data-testid={`btn-delete-ll-${ll._id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Regenerate Key Confirmation Dialog */}
      {showRegenerateDialog && (
        <div className="dialog-overlay" data-testid="regenerate-dialog">
          <div className="dialog-box">
            <h3>⚠️ Regenerate OpenClaw API Key?</h3>
            <p>Regenerating will disconnect OpenClaw until the new key is configured.</p>
            <p className="dialog-warning">
              The old API key will be invalidated immediately. Any API calls using the old key will return 401 Unauthorized.
            </p>
            <div className="dialog-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowRegenerateDialog(false)}
                data-testid="btn-cancel-regenerate"
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleRegenerateKey}
                disabled={regeneratingKey}
                data-testid="btn-confirm-regenerate"
              >
                {regeneratingKey ? 'Regenerating...' : 'Yes, Regenerate Key'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* New API Key Reveal Dialog */}
      {showNewKeyDialog && (
        <div className="dialog-overlay" data-testid="new-key-dialog">
          <div className="dialog-box dialog-large">
            <h3>✅ New OpenClaw API Key Generated</h3>
            <p className="dialog-warning" style={{marginBottom: '1rem'}}>
              <strong>⚠️ IMPORTANT:</strong> This is the ONLY time you'll see the full key. Copy it now!
            </p>
            
            <div className="new-key-display">
              <label>Your New API Key:</label>
              <div className="new-key-value" data-testid="new-api-key-value">
                <code>{newlyGeneratedKey}</code>
              </div>
            </div>
            
            <div className="dialog-actions">
              <button 
                className="btn-primary"
                onClick={handleCopyNewKey}
                data-testid="btn-copy-new-key"
              >
                📋 Copy to Clipboard
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setShowNewKeyDialog(false)}
                data-testid="btn-close-new-key-dialog"
              >
                I've Copied It, Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Full Autonomy Confirmation Dialog */}
      {showAutonomyDialog && (
        <div className="dialog-overlay" data-testid="autonomy-dialog">
          <div className="dialog-box">
            <h3>⚠️ Enable Full Autonomy?</h3>
            <p>Are you sure you want to enable Full Autonomy mode?</p>
            <p className="dialog-warning">
              In Full Autonomy mode, emails are sent without your review. This includes routine emails only — legal and sensitive emails are always held for approval.
            </p>
            <div className="dialog-actions">
              <button 
                className="btn-secondary"
                onClick={cancelAutonomyModeChange}
                data-testid="btn-cancel-autonomy"
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={confirmAutonomyModeChange}
                data-testid="btn-confirm-autonomy"
              >
                I Understand, Enable Full Autonomy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Tenancy Tab */}
      {activeTab === 'pre_tenancy' && (
        <PreTenancySettingsTab />
      )}

      {/* 2026-06-04 — Maintenance settings (Part 3). */}
      {activeTab === 'maintenance' && (
        <MaintenanceSettingsTab />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="settings-content" data-testid="billing-tab">
          {billingLoading ? (
            <div className="loading">Loading billing info...</div>
          ) : billingInfo ? (
            <div className="billing-content">
              {/* Plan Info */}
              <div className="billing-plan-card" data-testid="billing-plan-card">
                <div className="billing-plan-header">
                  <h3>{billingInfo.plan_name}</h3>
                  <span className={`billing-status billing-status-${billingInfo.subscription_status}`} data-testid="billing-status-badge">
                    {billingInfo.subscription_status === 'trialing' ? 'Free Trial' : billingInfo.subscription_status?.replace('_', ' ')}
                  </span>
                </div>

                {billingInfo.subscription_status === 'trialing' && billingInfo.trial_days_remaining != null && (
                  <div className="billing-trial-info" data-testid="billing-trial-info">
                    <div className="billing-trial-bar">
                      <div className="billing-trial-fill" style={{ width: `${Math.max(0, ((60 - billingInfo.trial_days_remaining) / 60) * 100)}%` }}></div>
                    </div>
                    <p><strong>{billingInfo.trial_days_remaining} days remaining</strong> of 60-day free trial</p>
                  </div>
                )}

                {billingInfo.billing_cycle_start && (
                  <p className="billing-detail">Billing cycle started: {billingInfo.billing_cycle_start}</p>
                )}
              </div>

              {/* Usage */}
              <div className="billing-usage-card" data-testid="billing-usage-card">
                <h4>Monthly Usage</h4>
                <div className="billing-usage-row">
                  <span>AI Token Usage</span>
                  <strong>{formatInteger(billingInfo.monthly_token_usage || 0)} tokens</strong>
                </div>
              </div>

              {/* Actions */}
              <div className="billing-actions" data-testid="billing-actions">
                <button className="btn-primary" onClick={handleManageBilling} data-testid="btn-manage-billing">
                  Manage Billing
                </button>
                {billingInfo.subscription_tier === 'professional' && (
                  <button className="btn-primary btn-upgrade" onClick={handleManageBilling} data-testid="btn-upgrade">
                    Upgrade to Agency Pro — £849/mo
                  </button>
                )}
              </div>
              <p className="billing-portal-hint">Opens Stripe Customer Portal for payment methods, invoices, and subscription changes.</p>
            </div>
          ) : (
            <p>Unable to load billing information.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Settings;

/* ====================================================================
 * Pre-Tenancy Settings Tab (Step 33)
 * Agent pastes their referencing provider applicant URL (Goodlord,
 * Vouch, etc.) here. Stored against AgentClient and injected into the
 * outbound referencing message on the Lead detail page.
 * ==================================================================== */
function PreTenancySettingsTab() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/pre-tenancy/settings`, {
          headers: { 'Authorization': API_KEY },
        });
        if (res.ok) {
          const t = await res.text();
          const d = t ? JSON.parse(t) : {};
          setUrl(d.referencing_provider_url || '');
        }
      } catch (err) {
        console.error('Failed to load pre-tenancy settings', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await fetch(`${BACKEND_URL}/api/pre-tenancy/settings`, {
        method: 'PUT',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ referencing_provider_url: url.trim() || null }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="settings-content"><p>Loading...</p></div>;

  return (
    <div className="settings-content" data-testid="pre-tenancy-tab">
      <div className="settings-section">
        <h3>Referencing Provider</h3>
        <p className="settings-hint" style={{marginTop: '-0.25rem'}}>
          Paste the applicant link from your referencing provider (Goodlord, Vouch, RentProfile, etc.).
          Estatel AI will embed this URL in the referencing message sent from the Lead detail page.
        </p>
        <div className="form-field" style={{marginTop: '1rem'}}>
          <label htmlFor="ref-url">Referencing Provider URL</label>
          <input
            id="ref-url"
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://goodlord.co/apply/your-agency-code"
            data-testid="input-referencing-url"
          />
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginTop:'0.75rem'}}>
          <button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="btn-save-referencing-url"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && <span style={{color: '#059669', fontSize: '0.85rem'}} data-testid="pre-tenancy-saved">Saved</span>}
        </div>
      </div>
    </div>
  );
}

/* ====================================================================
 * Agency Details Tab — Item 1 (2026-05-06)
 *
 * First tab on the Settings page. Lets the agent view and edit the
 * AgentClient profile (contact + correspondence address). The address
 * fields populate Form 3A §5.5 ("Agent serving on behalf of landlord")
 * — Stage 3 of the Section 8 flow blocks notice generation if any of
 * address_line1 / city / postcode are missing.
 *
 * Endpoints:
 *   GET  /api/agency/profile
 *   PUT  /api/agency/profile
 *
 * Body parsing follows the project convention: await res.text() then
 * JSON.parse(...).
 * ==================================================================== */
function AgencyDetailsTab() {
  const EMPTY = {
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    address_line1: '', address_line2: '', city: '', county: '', postcode: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Load the current profile on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/agency/profile`, {
          headers: { 'Authorization': API_KEY },
        });
        const t = await res.text();
        const d = t ? JSON.parse(t) : {};
        if (res.ok) {
          setForm({
            company_name:  d.company_name  || '',
            contact_name:  d.contact_name  || '',
            contact_email: d.contact_email || '',
            contact_phone: d.contact_phone || '',
            address_line1: d.address_line1 || '',
            address_line2: d.address_line2 || '',
            city:          d.city          || '',
            county:        d.county        || '',
            postcode:      d.postcode      || '',
          });
        } else {
          setError(d.detail || 'Failed to load agency profile');
        }
      } catch (e) {
        setError('Network error: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const change = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    // Required-field guard mirroring the backend Pydantic schema so
    // the agent gets an immediate error before the network round-trip.
    const required = [
      ['company_name',  'Company name'],
      ['contact_name',  'Contact name'],
      ['contact_email', 'Contact email'],
      ['contact_phone', 'Contact phone'],
      ['address_line1', 'Address line 1'],
      ['city',          'Town or city'],
      ['postcode',      'Postcode'],
    ];
    for (const [k, label] of required) {
      if (!String(form[k] || '').trim()) {
        setError(`${label} is required.`);
        return;
      }
    }
    setSaving(true); setSaved(false); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/agency/profile`, {
        method: 'PUT',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name:  form.company_name.trim(),
          contact_name:  form.contact_name.trim(),
          contact_email: form.contact_email.trim(),
          contact_phone: form.contact_phone.trim(),
          address_line1: form.address_line1.trim(),
          address_line2: form.address_line2.trim() || null,
          city:          form.city.trim(),
          county:        form.county.trim() || null,
          postcode:      form.postcode.trim(),
        }),
      });
      const t = await res.text();
      const d = t ? JSON.parse(t) : {};
      if (!res.ok) {
        const msg = typeof d.detail === 'string'
          ? d.detail
          : Array.isArray(d.detail) && d.detail[0]?.msg
            ? d.detail[0].msg
            : `Failed to save (HTTP ${res.status})`;
        setError(msg);
        return;
      }
      setForm({
        company_name:  d.company_name  || '',
        contact_name:  d.contact_name  || '',
        contact_email: d.contact_email || '',
        contact_phone: d.contact_phone || '',
        address_line1: d.address_line1 || '',
        address_line2: d.address_line2 || '',
        city:          d.city          || '',
        county:        d.county        || '',
        postcode:      d.postcode      || '',
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-content" data-testid="agency-details-tab">
        <p>Loading agency profile…</p>
      </div>
    );
  }

  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };

  return (
    <div className="settings-content" data-testid="agency-details-tab">
      {/* SECTION — Agency Information */}
      <div className="settings-section" data-testid="agency-info-section">
        <h2>Agency Information</h2>
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 1.25rem' }}>
          <div style={fieldStyle}>
            <label htmlFor="ad-company">Company name *</label>
            <input id="ad-company" type="text" value={form.company_name}
                   onChange={change('company_name')}
                   data-testid="input-company-name" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-contact">Contact name *</label>
            <input id="ad-contact" type="text" value={form.contact_name}
                   onChange={change('contact_name')}
                   data-testid="input-contact-name" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-email">Contact email *</label>
            <input id="ad-email" type="email" value={form.contact_email}
                   onChange={change('contact_email')}
                   data-testid="input-contact-email" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-phone">Contact phone *</label>
            <input id="ad-phone" type="tel" value={form.contact_phone}
                   onChange={change('contact_phone')}
                   placeholder="+441234567890"
                   data-testid="input-contact-phone" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
          <button className="btn-primary" onClick={save} disabled={saving}
                  data-testid="btn-save-agency-info">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          {saved && (
            <span style={{ color: '#059669', fontSize: '0.85rem' }}
                  data-testid="agency-info-saved">Saved</span>
          )}
        </div>
      </div>

      {/* SECTION — Agency Address */}
      <div className="settings-section" data-testid="agency-address-section">
        <h2>Agency Address</h2>
        <p className="settings-hint" style={{ marginTop: '-0.25rem' }}>
          This address is used as the correspondence address on all
          Section 8 documents including Form 3A section 5.5.
        </p>
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 1.25rem', marginTop: '0.75rem' }}>
          <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
            <label htmlFor="ad-line1">Address line 1 *</label>
            <input id="ad-line1" type="text" value={form.address_line1}
                   onChange={change('address_line1')}
                   data-testid="input-address-line1" />
          </div>
          <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
            <label htmlFor="ad-line2">Address line 2</label>
            <input id="ad-line2" type="text" value={form.address_line2}
                   onChange={change('address_line2')}
                   data-testid="input-address-line2" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-city">Town or city *</label>
            <input id="ad-city" type="text" value={form.city}
                   onChange={change('city')}
                   data-testid="input-city" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-county">County</label>
            <input id="ad-county" type="text" value={form.county}
                   onChange={change('county')}
                   data-testid="input-county" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="ad-postcode">Postcode *</label>
            <input id="ad-postcode" type="text" value={form.postcode}
                   onChange={change('postcode')}
                   data-testid="input-postcode" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
          <button className="btn-primary" onClick={save} disabled={saving}
                  data-testid="btn-save-agency-address">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          {saved && (
            <span style={{ color: '#059669', fontSize: '0.85rem' }}
                  data-testid="agency-address-saved">Saved</span>
          )}
        </div>
      </div>

      {error && (
        <div role="alert"
             style={{ background: '#fef2f2', color: '#991b1b',
                      padding: '0.7rem 1rem', borderRadius: 6,
                      marginTop: '1rem', fontSize: '0.9rem' }}
             data-testid="agency-details-error">
          {error}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// 2026-06-04 — Maintenance settings (Part 3 of the Contractor spec)
// =============================================================================
function MaintenanceSettingsTab() {
  const [mode, setMode] = React.useState('managed_by_estateiq');
  const [highHours, setHighHours] = React.useState(2);
  const [mediumHours, setMediumHours] = React.useState(24);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/maintenance/settings`,
          { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setMode(d.maintenance_autonomy || 'managed_by_estateiq');
          setHighHours(d.high_priority_escalation_hours || 2);
          setMediumHours(d.medium_priority_escalation_hours || 24);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setErr(null); setSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/maintenance/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          maintenance_autonomy: mode,
          high_priority_escalation_hours: Number(highHours) || 2,
          medium_priority_escalation_hours: Number(mediumHours) || 24,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Could not save.');
        return;
      }
      setSaved(true);
      window.dispatchEvent(new Event('maintenance-settings-saved'));
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="settings-content" data-testid="maintenance-tab-loading">Loading…</div>;
  }

  const OPTION = (id, title, body) => (
    <label key={id} data-testid={`maint-mode-${id}`}
           style={{ display: 'block', border: mode === id ? '2px solid #ea580c' : '1px solid #e2e8f0',
                    background: mode === id ? '#fff7ed' : '#fff',
                    borderRadius: 12, padding: '1rem 1.1rem', marginBottom: 12,
                    cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <input type="radio" name="maint-mode" value={id} checked={mode === id}
               onChange={() => setMode(id)}
               style={{ marginTop: 4 }} />
        <div>
          <strong style={{ color: '#0f172a' }}>{title}</strong>
          <p style={{ margin: '4px 0 0', color: '#475569', fontSize: '0.9rem' }}>{body}</p>
        </div>
      </div>
    </label>
  );

  return (
    <div className="settings-content" data-testid="maintenance-tab">
      <h2 style={{ marginTop: 0 }}>Maintenance coordination</h2>
      <p style={{ color: '#64748b', maxWidth: 720 }} data-testid="maint-mode-scope-note">
        Applies to maintenance contractor coordination only. Section 8, arrears tracking and compliance monitoring carry on either way, and you still receive every alert.
      </p>

      {OPTION('managed_by_estateiq', 'Estatel AI manages',
        'Estatel AI coordinates each job end to end: contractor outreach, availability, booking and quotes.')}
      {OPTION('managed_by_agent', 'Agent manages',
        'Estatel AI acknowledges the tenant and puts the job on your board. No contractor is contacted and nothing is booked automatically. Arrange it yourself, or hand a job to Estatel AI from its card.')}

      <h3 style={{ marginTop: 28 }}>Escalation Time Windows</h3>
      <p style={{ color: '#64748b', maxWidth: 720 }}>
        When a handyman is contacted first, how long should Estatel AI wait
        before escalating to specialist contractors?
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: 18, maxWidth: 560 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem' }}>
            High priority — auto-escalate after (hours)
          </span>
          <input type="number" min={1} max={168} value={highHours}
                 data-testid="maint-high-hours"
                 onChange={e => setHighHours(e.target.value)}
                 style={{ padding: '0.55rem 0.75rem', border: '1px solid #cbd5e1',
                          borderRadius: 8, fontSize: '0.95rem' }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem' }}>
            Medium priority — agent prompt after (hours)
          </span>
          <input type="number" min={1} max={168} value={mediumHours}
                 data-testid="maint-medium-hours"
                 onChange={e => setMediumHours(e.target.value)}
                 style={{ padding: '0.55rem 0.75rem', border: '1px solid #cbd5e1',
                          borderRadius: 8, fontSize: '0.95rem' }} />
        </label>
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={save} disabled={saving}
                data-testid="maint-save"
                style={{ background: '#ea580c', color: '#fff', border: 'none',
                         padding: '0.65rem 1.5rem', borderRadius: 8,
                         cursor: saving ? 'not-allowed' : 'pointer',
                         opacity: saving ? 0.7 : 1, fontWeight: 600 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span data-testid="maint-saved-msg" style={{ color: '#166534' }}>Saved.</span>}
        {err && <span style={{ color: '#b91c1c' }}>{err}</span>}
      </div>
      <ComplianceHandlingSettings />
    </div>
  );
}
